<template>
    <div id="FormBuilder">
        <div class="header">
            <div class="header-text">
                <div :class="{ active: activeTab === 'create' }" @click="setActiveTab('create')">
                    {{ $t("form.create") }}
                </div>
                <div :class="{ active: activeTab === 'settings' }" @click="setActiveTab('settings')">
                    {{ $t("form.settings") }}
                </div>
                <div :class="{ active: activeTab === 'publish' }" @click="setActiveTab('publish')">
                    {{ $t("form.load") }}
                </div>
            </div>
        </div>
        <div v-if="activeTab === 'create'" id="form-builder">
            <div class="edit-menu">
                <div v-if="!openMenu" class="edit-menu-open" @click="openEditMenu">
                    {{ $t("form.insertFormField") }}
                    <span class="md-icon">add</span>
                </div>
                <transition name="slide">
                    <ItemMenu v-if="openMenu" class="item-menu" @close="closeEditMenu" />
                </transition>
            </div>
            <div class="edit-form-content" @dragover.prevent="onDragOver" @drop="onDrop" @dragenter="onDragEnter" @dragleave="onDragLeave">
                <div v-if="form.logo.length" style="display:flex;justify-content: center; margin: auto; padding: 5px">
                    <div v-for="logo in form.logo" :key="logo._id" style="display: flex">
                        <img
                            :src="logo.v2Format && logo.cdnUrl
                                ? logo.cdnUrl
                                : `https://static.supsis.live/${logo.path + logo._id}.${logo.ext}`"
                            :alt="logo.name"
                            :style="{ width: logo.width + 'px', height: logo.height + 'px', objectFit: 'contain' }"
                        />
                        <span
                            class="md-icon"
                            @click="deleteLogo(logo._id)"
                            style="color: #ff411cf5; cursor: pointer"
                            v-tippy="{ content: $t('form.clickToDeleteYourLogo'), placement: 'top' }"
                        >
                            delete
                        </span>
                        <span
                            class="md-icon"
                            style="cursor:pointer"
                            @click="showLogoSettingsModal = true"
                            v-tippy="{ content: $t('form.clickToDeleteYourLogo'), placement: 'top' }"
                        >
                            settings
                        </span>
                        <a-modal
                            v-model="showLogoSettingsModal"
                            title="Logo Settings"
                            @ok="updateLogoDimensions"
                            @cancel="showLogoSettingsModal = false"
                        >
                            <div class="form-group">
                                <label for="logoHeight">Logo Height</label>
                                <input type="number" v-model="logoHeight" id="logoHeight" />
                            </div>
                            <div class="form-group">
                                <label for="logoWidth">Logo Width</label>
                                <input type="number" v-model="logoWidth" id="logoWidth" />
                            </div>
                        </a-modal>
                    </div>
                </div>
                <div class="add-logo" @click="openFileInput" v-else>
                    <div class="line"></div>
                    <input id="fileInput" type="file" style="display: none" @change="uploadFile" />
                    <div class="text">+ {{ $t("form.addYourLogo") }}</div>
                    <div class="line"></div>
                </div>
                <div class="form-edit">
                    <draggable
                        v-model="formItems"
                        @end="onEnd"
                        :item-key="getItemKey"
                        :options="dragOptions"
                    >
                        <div
                            v-for="(item, index) in formItems"
                            :key="item._id || item.slug || ('idx-'+index)"
                            class="form-item"
                            :class="{ 
                                active: activeIndex === index,
                                'drag-over-before': isDragging && dragOverIndex === index,
                                'drag-over-after': isDragging && dragOverIndex === index + 1 && index === formItems.length - 1
                            }"
                            @click="setActive(index)"
                            :data-index="index"
                        >
                            <!-- Drop indicator before item -->
                            <div 
                                v-if="isDragging && dragOverIndex === index" 
                                class="drop-indicator drop-before"
                            ></div>
                            <Title
                                :key="item._id + '-title'"
                                v-if="item.type === 'title'"
                                :item="item"
                                :title-size="item.titleSize"
                                :title-align="item.titleAlign"
                                :title-text="item.titleText"
                                :sub-title="item.subTitle"
                                @update-field="updateFieldFrom(item._id, $event)"
                            />
                            <FullName
                                :key="item._id + '-fullName'"
                                v-if="item.type === 'fullName'"
                                :item="item"
                                :field-label="item.fieldLabel"
                                :full-name-align="item.fullNameAlign"
                                :first-name="item.firstName"
                                :last-name="item.lastName"
                                :is-required="item.isRequired"
                                @update-field="updateFieldFrom(item._id, $event)"
                            />
                            <Email
                                :key="item._id + '-email'"
                                v-if="item.type === 'email'"
                                :item="item"
                                :field-email="item.fieldEmail"
                                :mail-align="item.mailAlign"
                                :alt-label="item.altLabel"
                                :is-required="item.isRequired"
                                @update-field="updateFieldFrom(item._id, $event)"
                            />
                            <Address
                                :key="item._id + '-address'"
                                v-if="item.type === 'address'"
                                :field-address="item.fieldAddress"
                                :align="item.align"
                                :item="item"
                                :address-line1="item.addressLine1"
                                :address-line2="item.addressLine2"
                                :district="item.district"
                                :postal-code="item.postalCode"
                                :state="item.state"
                                :is-required="item.isRequired"
                                @update-field="updateFieldFrom(item._id, $event)"
                            />
                            <Phone
                                :key="item._id + '-phone'"
                                v-if="item.type === 'phone'"
                                :item="item"
                                :field-phone="item.fieldPhone"
                                :align="item.align"
                                :masked="item.masked"
                                :is-required="item.isRequired"
                                @update-field="updateFieldFrom(item._id, $event)"
                            />
                            <DatePicker
                                :key="item._id + '-datePicker'"
                                v-if="item.type === 'datePicker'"
                                :item="item"
                                :align="item.align"
                                :date-label="item.dateLabel"
                                :field-label="item.datePicker.fieldLabel"
                                :is-required="item.isRequired"
                                @update-field="updateFieldFrom(item._id, $event)"
                            />
                            <Meeting v-if="item.type === 'meeting'" />
                            <Signature
                                :key="item._id + '-signature'"
                                v-if="item.type === 'signature'"
                                :fieldLabel="item.fieldLabel"
                                :align="item.align"
                                :altLabel="item.altLabel"
                                :isRequired="item.isRequired"
                                :canvasWidth="item.canvasWidth"
                                :canvasHeight="item.canvasHeight"
                                :strokeColor="item.strokeColor"
                                :strokeWidth="item.strokeWidth"
                                :backgroundColor="item.backgroundColor"
                                :borderColor="item.borderColor"
                                :borderWidth="item.borderWidth"
                                :borderRadius="item.borderRadius"
                                :showClearButton="item.showClearButton"
                                :clearButtonText="item.clearButtonText"
                                :placeholder="item.placeholder"
                                :item="item"
                                @update-field="updateFieldFrom(item._id, $event)"
                            />
                            <Captcha
                                :key="item._id + '-captcha'"
                                v-if="item.type === 'captcha'"
                                :fieldLabel="item.fieldLabel"
                                :align="item.align"
                                :placeholder="item.placeholder"
                                :isRequired="item.isRequired"
                                :item="item"
                                @update-field="updateFieldFrom(item._id, $event)"
                            />
                            <PageBreak
                                :key="item._id + '-pagebreak'"
                                v-if="item.type === 'pagebreak'"
                                :align="item.align"
                                :nextButtonText="item.nextButtonText"
                                :previousButtonText="item.previousButtonText"
                                :showPreviousButton="item.showPreviousButton"
                                :showNextButton="item.showNextButton"
                                :item="item"
                                :isSelected="settingsMenuItem && settingsMenuItem._id === item._id"
                                @update-field="updateFieldFrom(item._id, $event)"
                                @select-item="selectItem"
                            />
                            <ListOfProducts v-if="item.type === 'listOfProducts'" />
                            <ShortText
                                :key="item._id + '-shortText'"
                                v-if="item.type === 'shortText'"
                                :field-label="item.fieldLabel"
                                :item="item"
                                :align="item.align"
                                :alt-label="item.altLabel"
                                :is-required="item.isRequired"
                                @update-field="updateFieldFrom(item._id, $event)"
                            />
                            <LongText
                                :key="item._id + '-longText'"
                                v-if="item.type === 'longText'"
                                :field-label="item.fieldLabel"
                                :align="item.align"
                                :item="item"
                                :alt-label="item.altLabel"
                                :is-required="item.isRequired"
                                @update-field="updateFieldFrom(item._id, $event)"
                            />
                            <Paragraph
                                v-if="item.type === 'paragraph'"
                                :item="item"
                                :is-active="activeItemId === item._id"
                                @update-field="updateFieldFrom(item._id, $event)"
                            />
                            <DropDownList
                                :key="item._id + '-dropDownList'"
                                v-if="item.type === 'dropDownList'"
                                :item="item"
                                :field-label="item.fieldLabel"
                                :placeholder="item.placeholder"
                                :align="item.align"
                                :alt-label="item.altLabel"
                                :is-required="item.isRequired"
                                @update-field="updateFieldFrom(item._id, $event)"
                            />
                            <SingleChoice
                                :key="item._id + '-singleChoice'"
                                v-if="item.type === 'singleChoice'"
                                :item="item"
                                :field-label="item.fieldLabel"
                                :align="item.align"
                                :is-required="item.isRequired"
                                @update-field="updateFieldFrom(item._id, $event)"
                            />
                            <MultipleSelection
                                :key="item._id + '-multipleSelection'"
                                v-if="item.type === 'multipleSelection'"
                                :item="item"
                                :field-label="item.fieldLabel"
                                :align="item.align"
                                :is-required="item.isRequired"
                                @update-field="updateFieldFrom(item._id, $event)"
                            />
                            <Number
                                :key="item._id + '-number'"
                                v-if="item.type === 'number'"
                                :item="item"
                                :field-label="item.fieldLabel"
                                :align="item.labelAlign"
                                :sub-label="item.subLabel"
                                :is-required="item.isRequired"
                                @update-field="updateFieldFrom(item._id, $event)"
                            />
                            <FormImage
                                v-if="item.type === 'image'"
                                :item="item"
                                :align="item.align"
                                :asset="item.asset"
                                :imageWidth="item.imageWidth"
                                :imageHeight="item.imageHeight"
                            />
                            <FileUpload
                                v-if="item.type === 'fileUpload'"
                                :item="item"
                                :field-label-file="item.fieldLabelFile"
                                :align="item.align"
                                :alt-label-one="item.altLabelOne"
                                :alt-label-two="item.altLabelTwo"
                            />
                            <Hour
                                :key="item._id + '-hour'"
                                :item="item"
                                v-if="item.type === 'hour'"
                                :field-label="item.fieldLabel"
                                :align="item.align"
                                :hour-label="item.hourLabel"
                                :minute-label="item.minuteLabel"
                                @update-field="updateFieldFrom(item._id, $event)"
                            />
                            <Spinner
                                :key="item._id + '-spinner'"
                                :item="item"
                                v-if="item.type === 'spinner'"
                                :field-label="item.fieldLabel"
                                :align="item.align"
                                :is-required="item.isRequired"
                                @update-field="updateFieldFrom(item._id, $event)"
                            />
                            <Send
                                v-if="item.type === 'send'"
                                :item="item"
                                :label="item.fieldLabel"
                                :align="item.align"
                            />
                            <DataTable v-if="item.type === 'dataTable'" />
                            <StarRating
                                :key="item._id + '-starRating'"
                                :item="item"
                                v-if="item.type === 'starRating'"
                                :field-label="item.fieldLabel"
                                :align="item.align"
                                @update-field="updateFieldFrom(item._id, $event)"
                            />
                            <RatingScale
                                :key="item._id + '-ratingScale'"
                                :item="item"
                                v-if="item.type === 'ratingScale'"
                                :field-label="item.fieldLabel"
                                :align="item.align"
                                :sub-label-one="item.subLabelOne"
                                :sub-label-two="item.subLabelTwo"
                                @update-field="updateFieldFrom(item._id, $event)"
                            />
                            <SeparationElement
                                v-if="item.type === 'separationElement'"
                                :separation-color="item.separationColor"
                                :separator-bottom="item.separatorBottom"
                                :separator-height="item.separatorHeight"
                                :separator-side="item.separatorSide"
                                :separator-style="item.separatorStyle"
                                :separator-top="item.separatorTop"
                            />
                            <CollapseSection v-if="item.type === 'collapseSection'" />
                            <PageBreak v-if="item.type === 'pageBreak'" />
                            <div class="form-controls" :class="{ visible: activeIndex === index }">
                                <span class="md-icon" @click.stop="removeItem(index)">delete</span>
                                <span class="md-icon" @click.stop="openSettings(item, item.type)">settings</span>
                            </div>
                            
                            <!-- Drop indicator after item -->
                            <div 
                                v-if="isDragging && dragOverIndex === index + 1" 
                                class="drop-indicator drop-after"
                            ></div>
                        </div>
                    </draggable>
                </div>
            </div>
            <div class="right-menu">
                <transition name="slideRight">
                    <div v-if="openSettingsMenu" class="settings-menu">
                        <SettingsMenu
                            :item="settingsMenuItem"
                            :type="settingsMenuType"
                            @close="closeSettingsMenu"
                            @update-title-size="updateTitleSize"
                            @update-text-align="updateTextAlign"
                            @update-title-text="updateTitleText"
                            @update-subtitle-text="updateSubTitleText"
                            @update-field-label="updateFieldLabel"
                            @update-label-align="updateLabelAlign"
                            @update-first-name="updateFirstName"
                            @update-last-name="updateLastName"
                            @update-visibility="updateVisibility"
                            @update-slug="updateSlug"
                            @update-field-email="updateFieldEmail"
                            @update-label-align-mail="updateLabelAlignMail"
                            @update-alt-label-mail="updateAltLabelMail"
                            @update-field-label-address="updateFieldLabelAddress"
                            @update-label-align-address="updateLabelAlignAddress"
                            @update-field-address-line-1="updateFieldAddressLine1"
                            @update-field-address-line-2="updateFieldAddressLine2"
                            @update-field-district="updateFieldDistrict"
                            @update-field-postal-code="updateFieldPostalCode"
                            @update-field-state="updateFieldState"
                            @update-field-phone="updateFieldPhone"
                            @update-label-align-phone="updateLabelAlignPhone"
                            @update-masked-phone="updateMaskedPhone"
                            @update-field-label-date-picker="updateFieldLabelDatePicker"
                            @update-label-align-date-picker="updateLabelAlignDatePicker"
                            @update-date-label-date-picker="updateDateLabelDatePicker"
                            @update-field-label-short-text="updateFieldLabelShortText"
                            @update-label-align-short-text="updateLabelAlignShortText"
                            @update-alt-label-short-text="updateAltLabelShortText"
                            @update-field-label-long-text="updateFieldLabelLongText"
                            @update-label-align-long-text="updateLabelAlignLongText"
                            @update-alt-label-long-text="updateAltLabelLongText"
                            @update-margin-top="updateMarginTopParagraph"
                            @update-margin-bottom="updateMarginBottomParagraph"
                            @update-signature-field-label="updateSignatureFieldLabel"
                            @update-signature-align="updateSignatureAlign"
                            @update-signature-alt-label="updateSignatureAltLabel"
                            @update-signature-is-required="updateSignatureIsRequired"
                            @update-signature-canvas-width="updateSignatureCanvasWidth"
                            @update-signature-canvas-height="updateSignatureCanvasHeight"
                            @update-signature-stroke-color="updateSignatureStrokeColor"
                            @update-signature-stroke-width="updateSignatureStrokeWidth"
                            @update-signature-background-color="updateSignatureBackgroundColor"
                            @update-signature-border-color="updateSignatureBorderColor"
                            @update-signature-border-width="updateSignatureBorderWidth"
                            @update-signature-border-radius="updateSignatureBorderRadius"
                            @update-signature-show-clear-button="updateSignatureShowClearButton"
                            @update-signature-clear-button-text="updateSignatureClearButtonText"
                            @update-signature-placeholder="updateSignaturePlaceholder"
                            @update-captcha-field-label="updateCaptchaFieldLabel"
                            @update-captcha-align="updateCaptchaAlign"
                            @update-captcha-placeholder="updateCaptchaPlaceholder"
                            @update-captcha-is-required="updateCaptchaIsRequired"
                            @update-pagebreak-align="updatePagebreakAlign"
                            @update-pagebreak-next-button-text="updatePagebreakNextButtonText"
                            @update-pagebreak-previous-button-text="updatePagebreakPreviousButtonText"
                            @update-pagebreak-show-previous-button="updatePagebreakShowPreviousButton"
                            @update-pagebreak-show-next-button="updatePagebreakShowNextButton"
                            @update-field-label-drop-down-list="updateFieldLabelDropDownList"
                            @update-placeholder-drop-down-list="updatePlaceholderDropDownList"
                            @update-label-align-drop-down-list="updateLabelAlignDropDownList"
                            @update-alt-label-drop-down-list="updateAltLabelDropDownList"
                            @update-field-label-single-choice="updateFieldLabelSingleChoice"
                            @update-label-align-single-choice="updateLabelAlignSingleChoice"
                            @update-field-label-multiple-selection="updateFieldLabelMultipleSelection"
                            @update-label-align-multiple-selection="updateLabelAlignMultipleSelection"
                            @update-field-label-number="updateFieldLabelNumber"
                            @update-label-align-number="updateLabelAlignNumber"
                            @update-sub-label-number="updateSubLabelNumber"
                            @update-field-label-hour="updateFieldLabelHour"
                            @update-label-align-hour="updateLabelAlignHour"
                            @update-hour-label="updateHourLabel"
                            @update-minute-label="updateMinuteLabel"
                            @update-field-label-spinner="updateFieldLabelSpinner"
                            @update-label-align-spinner="updateLabelAlignSpinner"
                            @update-field-label-star-rating="updateFieldLabelStarRating"
                            @update-alignment-star-rating="updateAlignmentStarRating"
                            @update-field-label-rating-scale="updateFieldLabelRatingScale"
                            @update-field-label-send="updateFieldLabelSend"
                            @update-label-align-send="updateAlignmentSend"
                            @update-alignment-rating-scale="updateAlignmentRatingScale"
                            @update-sub-label-one-rating-scale="updateRatingScaleSubLabelOne"
                            @update-sub-label-two-rating-scale="updateRatingScaleSubLabelTwo"
                            @update-separation-color="updateSeparationColor"
                            @update-separator-style="updateSeparatorStyle"
                            @update-separator-height="updateSeparatorHeight"
                            @update-separator-bottom="updateSeparatorBottom"
                            @update-separator-top="updateSeparatorTop"
                            @update-separator-side="updateSeparatorSide"
                            @update-file-label="updateFileLabel"
                            @update-file-alt-label-one="updateFileAltLabelOne"
                            @update-file-alt-label-two="updateFileAltLabelTwo"
                            @update-file-align="updateFileAlign"
                            @update-required="updateRequired"
                            @update-image-url="updateImageUrl"
                            @update-image-width="updateImageWidth"
                            @update-image-height="updateImageHeight"
                            @update-image-align="updateImageAlign"
                        />
                    </div>
                </transition>
            </div>
        </div>
        <div v-if="activeTab === 'settings'">
            <Settings :form-id="formId" :site-id="site._id" />
        </div>
        <div v-if="activeTab === 'publish'">
            <Publish :form-id="formId" :site-id="site._id" />
        </div>
    </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import ItemMenu from "./ItemMenu.vue";
import draggable from "vuedraggable";
import SettingsMenu from "./ItemSettingsMenu/SettingsMenu.vue";
import Title from "./Item/Title.vue";
import FullName from "./Item/FullName.vue";
import Email from "./Item/Email.vue";
import Address from "./Item/Address.vue";
import Phone from "./Item/Phone.vue";
import DatePicker from "./Item/DatePicker.vue";
import Meeting from "./Item/Meeting.vue";
import Signature from "./Item/Signature.vue";
import Captcha from "./Item/Captcha.vue";
import ListOfProducts from "./Item/ListOfProducts.vue";
import ShortText from "./Item/ShortText.vue";
import LongText from "./Item/LongText.vue";
import Paragraph from "./Item/Paragraph.vue";
import DropDownList from "./Item/DropDownList.vue";
import SingleChoice from "./Item/SingleChoice.vue";
import MultipleSelection from "./Item/MultipleSelection.vue";
import Number from "./Item/Number.vue";
import FormImage from "./Item/FormImage.vue";
import FileUpload from "./Item/FileUpload.vue";
import Hour from "./Item/Hour.vue";
import Spinner from "./Item/Spinner.vue";
import Send from "./Item/Send.vue";
import DataTable from "./Item/DataTable.vue";
import StarRating from "./Item/StarRating.vue";
import RatingScale from "./Item/RatingScale.vue";
import SeparationElement from "./Item/SeparationElement.vue";
import CollapseSection from "./Item/CollapseSection.vue";
import PageBreak from "./Item/PageBreak.vue";
import Settings from "./FormSettings/Settings.vue";
import Publish from "./FormPublish/Publish.vue";
import { uploadAsset } from "../../../../api/assetUpload";
import axios from "axios";

export default {
    name: "FormBuilder",
    components: {
        ItemMenu,
        draggable,
        SettingsMenu,
        Title,
        FullName,
        Email,
        Address,
        Phone,
        DatePicker,
        Meeting,
        Signature,
        Captcha,
        PageBreak,
        ListOfProducts,
        ShortText,
        LongText,
        Paragraph,
        DropDownList,
        SingleChoice,
        MultipleSelection,
        Number,
        FormImage,
        FileUpload,
        Hour,
        Spinner,
        Send,
        DataTable,
        StarRating,
        RatingScale,
        SeparationElement,
        CollapseSection,
        PageBreak,
        Settings,
        Publish,
    },
    props: {
        formId: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            showLogoSettingsModal: false,
            logoHeight: null,
            logoWidth: null,
            activeTab: "create",
            openMenu: false,
            formItems: [],
            activeIndex: null,
            activeItemId: null,
            openSettingsMenu: false,
            settingsMenuType: "",
            settingsMenuItem: null,
            settingsMenuIndex: null,
            form: {
                logo: [],
            },
            dragSaveTimer: null,
            isDragging: false,
            dragOverIndex: -1,
            dragOverThrottle: null,
        };
    },
    computed: {
        ...mapState({
            site: (state) => state.site.site,
        }),
        dragOptions() {
            return {
                animation: 150,
                ghostClass: 'drag-ghost',
                chosenClass: 'drag-chosen',
                dragClass: 'drag-drag',
                fallbackTolerance: 5,
                forceFallback: true,
                delayOnTouchOnly: true,
            };
        },
    },
    async mounted() {
        await this.loadForm();
    },
    methods: {
        ...mapActions({
            getFormById: "forms/getFormById",
            updateForm: "forms/updateForm",
            updateFormData: "forms/updateFormData",
            deleteFormField: "forms/deleteFormField",
        }),
        openLogoSettings(logo) {
            this.logoHeight = logo.height;
            this.logoWidth = logo.width;
            this.showLogoSettingsModal = true;
        },
        async updateLogoDimensions() {
            const updatedLogo = {
                id: this.form.logo[0]._id,
                height: this.logoHeight,
                width: this.logoWidth,
            };
            const updatedFormData = {
                ...this.form,
                logo: [updatedLogo],
            };
            try {
                await this.updateFormData({
                    siteId: this.form.siteId,
                    formId: this.form._id,
                    formData: updatedFormData,
                });
                this.$toasted.success(this.$t("message.successImgUpload"), {
                    duration: 1000,
                    position: "bottom-center",
                });
                this.loadForm();
            } catch (e) {
                this.$toasted.error(this.$t("message.errorImgUpload"), {
                    duration: 1000,
                    position: "bottom-center",
                });
            } finally {
                this.showLogoSettingsModal = false;
            }
        },
        openFileInput() {
            document.getElementById("fileInput").click();
        },
        async loadForm(retryCount = 0) {
            const { siteId, formId } = this;
            if (!formId) {
                console.error("Form ID is null, cannot load form.");
                return;
            }
            try {
                const { form, formFields } = await this.getFormById({ siteId, formId });
                if (!form || !formFields) {
                    throw new Error("Form or formFields is null");
                }
                this.form = form;
                this.form.title = form.title;
                this.form.description = form.description;
                this.formItems = formFields
                    .map((field) => {
                        const baseItem = {
                            ...field,
                            order: field.order,
                            label: field.label,
                            type: field.type,
                        };

                        this.ensureSlugForField(baseItem, formFields);

                        if (field.type === 'title' && field.title) {
                            baseItem.titleText = field.title.titleText;
                            baseItem.subTitle = field.title.subTitle;
                            baseItem.titleSize = field.title.titleSize;
                            baseItem.titleAlign = field.title.titleAlign;
                            baseItem.title = {
                                titleText: field.title.titleText,
                                subTitle: field.title.subTitle,
                                titleSize: field.title.titleSize,
                                titleAlign: field.title.titleAlign,
                            };
                        }

                        if (field.type === 'fullName' && field.fullName) {
                            baseItem.fieldLabel = field.fullName.fieldLabel;
                            baseItem.fullNameAlign = field.fullName.fullNameAlign;
                            baseItem.firstName = field.fullName.firstName;
                            baseItem.lastName = field.fullName.lastName;
                            baseItem.isRequired = field.fullName.isRequired;
                        }

                        if (field.type === 'email' && field.email) {
                            baseItem.fieldEmail = field.email.fieldEmail;
                            baseItem.mailAlign = field.email.mailAlign;
                            baseItem.altLabel = field.email.altLabel;
                            baseItem.isRequired = field.email.isRequired;
                        }

                        if (field.type === 'address' && field.address) {
                            baseItem.fieldAddress = field.address.fieldAddress;
                            baseItem.align = field.address.align;
                            baseItem.addressLine1 = field.address.addressLine1;
                            baseItem.addressLine2 = field.address.addressLine2;
                            baseItem.district = field.address.district;
                            baseItem.postalCode = field.address.postalCode;
                            baseItem.state = field.address.state;
                            baseItem.isRequired = field.address.isRequired;
                        }

                        if (field.type === 'phone' && field.phone) {
                            baseItem.fieldPhone = field.phone.fieldPhone;
                            baseItem.align = field.phone.align;
                            baseItem.masked = field.phone.masked;
                            baseItem.isRequired = field.phone.isRequired;
                        }

                        if (field.type === 'datePicker' && field.datePicker) {
                            baseItem.fieldLabel = field.datePicker.fieldLabel;
                            baseItem.align = field.datePicker.align;
                            baseItem.dateLabel = field.datePicker.dateLabel;
                            baseItem.isRequired = field.datePicker.isRequired;
                        }

                        if (field.type === 'shortText' && field.shortText) {
                            baseItem.fieldLabel = field.shortText.fieldLabel;
                            baseItem.align = field.shortText.align;
                            baseItem.altLabel = field.shortText.altLabel;
                            baseItem.isRequired = field.shortText.isRequired;
                        }

                        if (field.type === 'longText' && field.longText) {
                            baseItem.fieldLabel = field.longText.fieldLabel;
                            baseItem.align = field.longText.align;
                            baseItem.altLabel = field.longText.altLabel;
                            baseItem.isRequired = field.longText.isRequired;
                        }

                        if (field.type === 'paragraph' && field.paragraph) {
                            baseItem.paragraph = {
                                content: field.paragraph.content,
                                align: field.paragraph.align,
                                marginTop: field.paragraph.marginTop || 0,
                                marginBottom: field.paragraph.marginBottom || 0,
                            };
                        }

                        if (field.type === 'dropDownList' && field.dropDownList) {
                            baseItem.fieldLabel = field.dropDownList.fieldLabel;
                            baseItem.align = field.dropDownList.align;
                            baseItem.altLabel = field.dropDownList.altLabel;
                            baseItem.isRequired = field.dropDownList.isRequired;
                            baseItem.items = field.dropDownList.items;
                            baseItem.placeholder = field.dropDownList.placeholder;
                        }

                        if (field.type === 'singleChoice' && field.singleChoice) {
                            baseItem.fieldLabel = field.singleChoice.fieldLabel;
                            baseItem.align = field.singleChoice.align;
                            baseItem.isRequired = field.singleChoice.isRequired;
                            baseItem.items = field.singleChoice.items;
                            baseItem.otherOption = field.singleChoice.otherOption;
                            baseItem.defaultValues = (field.singleChoice.defaultValues && Array.isArray(field.singleChoice.defaultValues)) ? field.singleChoice.defaultValues : [];
                        }

                        if (field.type === 'multipleSelection' && field.multipleSelection) {
                            baseItem.fieldLabel = field.multipleSelection.fieldLabel;
                            baseItem.align = field.multipleSelection.align;
                            baseItem.isRequired = field.multipleSelection.isRequired;
                            baseItem.items = field.multipleSelection.items;
                            baseItem.otherOption = field.multipleSelection.otherOption;
                            baseItem.defaultValues = (field.multipleSelection.defaultValues && Array.isArray(field.multipleSelection.defaultValues)) ? field.multipleSelection.defaultValues : [];
                        }

                        if (field.type === 'number' && field.number) {
                            baseItem.fieldLabel = field.number.fieldLabel;
                            baseItem.labelAlign = field.number.labelAlign;
                            baseItem.subLabel = field.number.subLabel;
                            baseItem.isRequired = field.number.isRequired;
                        }

                        if (field.type === 'hour' && field.hour) {
                            baseItem.fieldLabel = field.hour.fieldLabel;
                            baseItem.align = field.hour.align;
                            baseItem.hourLabel = field.hour.hourLabel;
                            baseItem.minuteLabel = field.hour.minuteLabel;
                            baseItem.isRequired = field.hour.isRequired;
                        }

                        if (field.type === 'spinner' && field.spinner) {
                            baseItem.fieldLabel = field.spinner.fieldLabel;
                            baseItem.align = field.spinner.align;
                            baseItem.isRequired = field.spinner.isRequired;
                        }

                        if (field.type === 'starRating' && field.starRating) {
                            baseItem.fieldLabel = field.starRating.fieldLabel;
                            baseItem.align = field.starRating.align;
                        }

                        if (field.type === 'ratingScale' && field.ratingScale) {
                            baseItem.fieldLabel = field.ratingScale.fieldLabel;
                            baseItem.align = field.ratingScale.align;
                            baseItem.subLabelOne = field.ratingScale.subLabelOne;
                            baseItem.subLabelTwo = field.ratingScale.subLabelTwo;
                        }

                        if (field.type === 'send' && field.send) {
                            baseItem.fieldLabel = field.send.fieldLabel;
                            baseItem.align = field.send.align;
                        }

                        if (field.type === 'separationElement' && field.separationElement) {
                            baseItem.separationColor = field.separationElement.separationColor;
                            baseItem.separatorStyle = field.separationElement.separatorStyle;
                            baseItem.separatorHeight = field.separationElement.separatorHeight;
                            baseItem.separatorBottom = field.separationElement.separatorBottom;
                            baseItem.separatorTop = field.separationElement.separatorTop;
                            baseItem.separatorSide = field.separationElement.separatorSide;
                        }

                        if (field.type === 'fileUpload' && field.fileUpload) {
                            baseItem.fieldLabelFile = field.fileUpload.fieldLabelFile;
                            baseItem.align = field.fileUpload.align;
                            baseItem.altLabelOne = field.fileUpload.altLabelOne;
                            baseItem.altLabelTwo = field.fileUpload.altLabelTwo;
                        }

                        if (field.type === 'image' && field.image) {
                            baseItem.asset = field.image.asset;
                            baseItem.imageWidth = field.image.width;
                            baseItem.imageHeight = field.image.height;
                            baseItem.align = field.image.align;
                        }

                        if (field.type === 'signature' && field.signature) {
                            baseItem.fieldLabel = field.signature.fieldLabel;
                            baseItem.align = field.signature.align;
                            baseItem.altLabel = field.signature.altLabel;
                            baseItem.isRequired = field.signature.isRequired;
                            baseItem.canvasWidth = field.signature.canvasWidth;
                            baseItem.canvasHeight = field.signature.canvasHeight;
                            baseItem.strokeColor = field.signature.strokeColor;
                            baseItem.strokeWidth = field.signature.strokeWidth;
                            baseItem.backgroundColor = field.signature.backgroundColor;
                            baseItem.borderColor = field.signature.borderColor;
                            baseItem.borderWidth = field.signature.borderWidth;
                            baseItem.borderRadius = field.signature.borderRadius;
                            baseItem.showClearButton = field.signature.showClearButton;
                            baseItem.clearButtonText = field.signature.clearButtonText;
                            baseItem.placeholder = field.signature.placeholder;
                        }

                        if (field.type === 'captcha' && field.captcha) {
                            baseItem.fieldLabel = field.captcha.fieldLabel;
                            baseItem.align = field.captcha.align;
                            baseItem.placeholder = field.captcha.placeholder;
                            baseItem.isRequired = field.captcha.isRequired;
                        }

                        if (field.type === 'pagebreak' && field.pagebreak) {
                            baseItem.fieldLabel = field.pagebreak.fieldLabel;
                            baseItem.align = field.pagebreak.align;
                            baseItem.nextButtonText = field.pagebreak.nextButtonText;
                            baseItem.previousButtonText = field.pagebreak.previousButtonText;
                            baseItem.showPreviousButton = field.pagebreak.showPreviousButton;
                            baseItem.showNextButton = field.pagebreak.showNextButton;
                        }

                        return baseItem;
                    })
                    .sort((a, b) => a.order - b.order);
                
                // DOM'un tam olarak render edilmesini bekle
                this.$nextTick(() => {
                    // Drag&drop için gerekli elementlerin render edilmesini sağla
                    setTimeout(() => {
                        console.log('✅ Form loaded and DOM ready for drag&drop');
                    }, 100);
                });
            } catch (error) {
                console.error("Error loading form:", error);
                if (retryCount < 3) {
                    setTimeout(() => {
                        this.loadForm(retryCount + 1);
                    }, 1000);
                } else {
                    console.error("Failed to load form after 3 retries");
                }
            }
        },
        async saveForm() {
            const { siteId, formId } = this;
            const formData = {
                title: this.form.title,
                description: this.form.description,
            };
            const formFieldsData = this.formItems.map((item, index) => {
                const baseData = {
                    _id: item._id,
                    type: item.type,
                    order: index,
                    slug: item.slug,
                };
                switch (item.type) {
                    case "title":
                        return {
                            ...baseData,
                            label: item.titleText || "Başlık",
                            slug: item.slug,
                            title: {
                                titleText: item.titleText || "Başlık",
                                subTitle: item.subTitle === undefined || item.subTitle === null ? "Alt Başlık" : item.subTitle,
                                titleSize: item.titleSize || "default",
                                titleAlign: item.titleAlign || "top",
                            },
                        };
                    case "fullName":
                        return {
                            ...baseData,
                            label: item.fieldLabel || "İsim",
                            fullName: {
                                fieldLabel: item.fieldLabel || "İsim",
                                fullNameAlign: item.fullNameAlign || "top",
                                firstName: item.firstName || "Ad",
                                lastName: item.lastName || "Soyad",
                                isRequired: item.isRequired || false,
                            },
                        };
                    case "email":
                        return {
                            ...baseData,
                            label: item.fieldEmail || "E-posta",
                            email: {
                                fieldEmail: item.fieldEmail || "E-posta",
                                mailAlign: item.mailAlign || "top",
                                altLabel: item.altLabel === undefined || item.altLabel === null ? "example@example.com" : item.altLabel,
                                isRequired: item.isRequired || false,
                            },
                        };
                    case "address":
                        return {
                            ...baseData,
                            label: item.fieldAddress || "Adres",
                            address: {
                                fieldAddress: item.fieldAddress || "Addres",
                                align: item.align || "top",
                                addressLine1: item.addressLine1 === undefined || item.addressLine1 === null ? "Adres Satırı 1" : item.addressLine1,
                                addressLine2: item.addressLine2 === undefined || item.addressLine2 === null ? "Adres Satırı 2" : item.addressLine2,
                                district: item.district === undefined || item.district === null ? "İlçe" : item.district,
                                postalCode: item.postalCode === undefined || item.postalCode === null ? "Posta Kodu" : item.postalCode,
                                state: item.state === undefined || item.state === null ? "Şehir" : item.state,
                                isRequired: item.isRequired || false,
                            },
                        };
                    case "phone":
                        return {
                            ...baseData,
                            label: item.fieldPhone || "Telefon",
                            phone: {
                                fieldPhone: item.fieldPhone || "Telefon",
                                align: item.align || "top",
                                masked: item.masked === undefined || item.masked === null ? "Lütfen telefon numaranızı giriniz" : item.masked,
                                isRequired: item.isRequired || false,
                            },
                        };
                    case "datePicker":
                        return {
                            ...baseData,
                            label: item.fieldLabelDatePicker || "Tarih",
                            datePicker: {
                                fieldLabel: item.fieldLabelDatePicker || "Tarih",
                                align: item.align || "top",
                                dateLabel: item.dateLabel === undefined || item.dateLabel === null ? "Tarih Seçiniz" : item.dateLabel,
                                isRequired: item.isRequired || false,
                            },
                        };
                    case "shortText":
                        return {
                            ...baseData,
                            label: item.fieldLabel || "Bir soru girin",
                            shortText: {
                                fieldLabel: item.fieldLabel || "Bir soru girin",
                                align: item.align || "top",
                                altLabel: item.altLabel === undefined || item.altLabel === null ? "Cevabınızı buraya yazınız" : item.altLabel,
                                isRequired: item.isRequired || false,
                            },
                        };
                    case "longText":
                        return {
                            ...baseData,
                            label: item.fieldLabel || "Bir soru girin",
                            longText: {
                                fieldLabel: item.fieldLabel || "Bir soru girin",
                                align: item.align || "top",
                                altLabel: item.altLabel === undefined || item.altLabel === null ? "Cevabınızı buraya yazınız" : item.altLabel,
                                isRequired: item.isRequired || false,
                            },
                        };
                    case "paragraph":
                        return {
                            ...baseData,
                            label: item.label || "Paragraf",
                            paragraph: {
                                content: item.paragraph && item.paragraph.content ? item.paragraph.content : '<p>Buraya bilgilendirici metninizi yazabilirsiniz...</p>',
                                align: item.paragraph && item.paragraph.align ? item.paragraph.align : 'left',
                                marginTop: item.paragraph && item.paragraph.marginTop !== undefined ? item.paragraph.marginTop : 0,
                                marginBottom: item.paragraph && item.paragraph.marginBottom !== undefined ? item.paragraph.marginBottom : 0,
                            },
                        };
                    case "dropDownList":
                        return {
                            ...baseData,
                            label: item.fieldLabel || "Bir soru girin",
                            dropDownList: {
                                fieldLabel: item.fieldLabel || "Bir soru girin",
                                align: item.align || "top",
                                altLabel: item.altLabel === undefined || item.altLabel === null ? "Seçim Yapınız..." : item.altLabel,
                                isRequired: item.isRequired || false,
                                items: item.items && item.items.length > 0 ? item.items : [],
                                placeholder: item.placeholder === undefined || item.placeholder === null ? "Seçim Yapınız..." : item.placeholder,
                            },
                        };
                    case "singleChoice":
                        return {
                            ...baseData,
                            label: item.fieldLabel || "Bir soru girin",
                            singleChoice: {
                                fieldLabel: item.fieldLabel || "Bir soru girin",
                                align: item.align || "top",
                                isRequired: item.isRequired || false,
                                items: item.items && item.items.length > 0 ? item.items : [],
                                otherOption: item.otherOption || "",
                                defaultValues: (item.defaultValues && Array.isArray(item.defaultValues)) ? item.defaultValues : [],
                            },
                        };
                    case "multipleSelection":
                        return {
                            ...baseData,
                            label: item.fieldLabel || "Bir soru girin",
                            multipleSelection: {
                                fieldLabel: item.fieldLabel || "Bir soru girin",
                                align: item.align || "top",
                                isRequired: item.isRequired || false,
                                items: item.items && item.items.length > 0 ? item.items : [],
                                otherOption: item.otherOption || "",
                                defaultValues: (item.defaultValues && Array.isArray(item.defaultValues)) ? item.defaultValues : [],
                            },
                        };
                    case "number":
                        return {
                            ...baseData,
                            label: item.fieldLabel || "Bir sayı girin",
                            number: {
                                fieldLabel: item.fieldLabel || "Bir sayı girin",
                                labelAlign: item.labelAlign || "top",
                                subLabel: item.subLabel === undefined || item.subLabel === null ? "Lütfen bir sayı giriniz" : item.subLabel,
                                isRequired: item.isRequired || false,
                            },
                        };
                    case "hour":
                        return {
                            ...baseData,
                            label: item.fieldLabel || "Saat",
                            hour: {
                                fieldLabel: item.fieldLabel || "Saat",
                                align: item.align || "top",
                                hourLabel: item.hourLabel === undefined || item.hourLabel === null ? "Saat" : item.hourLabel,
                                minuteLabel: item.minuteLabel === undefined || item.minuteLabel === null ? "Dakika" : item.minuteLabel,
                                isRequired: item.isRequired || false,
                            },
                        };
                    case "spinner":
                        return {
                            ...baseData,
                            label: item.fieldLabel || "Bir sayı girin",
                            spinner: {
                                fieldLabel: item.fieldLabel || "Bir sayı girin",
                                align: item.align || "top",
                                isRequired: item.isRequired || false,
                            },
                        };
                    case "starRating":
                        return {
                            ...baseData,
                            label: item.fieldLabel || "Bir sayı girin",
                            starRating: {
                                fieldLabel: item.fieldLabel || "Bir sayı girin",
                                align: item.align || "top",
                            },
                        };
                    case "ratingScale":
                        return {
                            ...baseData,
                            label: item.fieldLabel || "Bir sayı girin",
                            ratingScale: {
                                fieldLabel: item.fieldLabel || "Bir sayı girin",
                                align: item.align || "top",
                                subLabelOne: item.subLabelOne === undefined || item.subLabelOne === null ? "Düşük" : item.subLabelOne,
                                subLabelTwo: item.subLabelTwo === undefined || item.subLabelTwo === null ? "Yüksek" : item.subLabelTwo,
                            },
                        };
                    case "send":
                        return {
                            ...baseData,
                            label: item.fieldLabel || "Gönder",
                            send: {
                                fieldLabel: item.fieldLabel || "Gönder",
                                align: item.align || "top",
                            },
                        };
                    case "separationElement":
                        return {
                            ...baseData,
                            label: item.label,
                            separationElement: {
                                separationColor: item.separationColor || "#d3d3d3d3",
                                separatorStyle: item.separatorStyle || "solid",
                                separatorHeight: item.separatorHeight || "1",
                                separatorBottom: item.separatorBottom || "10",
                                separatorTop: item.separatorTop || "10",
                                separatorSide: item.separatorSide || "0",
                            },
                        };
                    case "fileUpload":
                        return {
                            ...baseData,
                            label: item.fieldLabelFile || "Dosya Yükle",
                            fileUpload: {
                                fieldLabelFile: item.fieldLabelFile || "Dosya Yükle",
                                align: item.align || "top",
                                altLabelOne: item.altLabelOne === undefined || item.altLabelOne === null ? "Dosya seçiniz" : item.altLabelOne,
                                altLabelTwo: item.altLabelTwo === undefined || item.altLabelTwo === null ? "Dosya seçiniz" : item.altLabelTwo,
                            },
                        };
                    case "image":
                        return {
                            ...baseData,
                            label: item.label,
                            image: {
                                asset: item.asset || "https://static.supsis.live/common/email/imageDefault.png",
                                width: item.width || "400",
                                height: item.height || "300",
                                align: item.align || "center",
                            },
                        };
                    case "signature":
                        return {
                            ...baseData,
                            label: item.fieldLabel || "İmza",
                            signature: {
                                fieldLabel: item.fieldLabel || "İmza",
                                align: item.align || "top",
                                altLabel: item.altLabel || "Lütfen imzanızı çiziniz",
                                isRequired: item.isRequired || false,
                                canvasWidth: item.canvasWidth || 400,
                                canvasHeight: item.canvasHeight || 200,
                                strokeColor: item.strokeColor || "#000000",
                                strokeWidth: item.strokeWidth || 2,
                                backgroundColor: item.backgroundColor || "#ffffff",
                                borderColor: item.borderColor || "#e5e7eb",
                                borderWidth: item.borderWidth || 1,
                                borderRadius: item.borderRadius || 8,
                                showClearButton: item.showClearButton !== undefined ? item.showClearButton : true,
                                clearButtonText: item.clearButtonText || "Temizle",
                                placeholder: item.placeholder || "Burada imza çizin",
                            },
                        };
                    case "captcha":
                        return {
                            ...baseData,
                            label: item.fieldLabel || "Güvenlik Doğrulaması",
                            captcha: {
                                fieldLabel: item.fieldLabel || "Güvenlik Doğrulaması",
                                align: item.align || "center",
                                placeholder: item.placeholder || "Yukarıdaki kodu girin",
                                isRequired: item.isRequired !== undefined ? item.isRequired : true,
                            },
                        };
                    case "pagebreak":
                        return {
                            ...baseData,
                            label: item.fieldLabel || "Sayfa Sonu",
                            pagebreak: {
                                fieldLabel: item.fieldLabel || "Sayfa Sonu",
                                align: item.align || "center",
                                nextButtonText: item.nextButtonText || "Devam",
                                previousButtonText: item.previousButtonText || "Geri",
                                showPreviousButton: item.showPreviousButton !== undefined ? item.showPreviousButton : true,
                                showNextButton: item.showNextButton !== undefined ? item.showNextButton : true,
                            },
                        };
                    default:
                        return baseData;
                }
            });
            await this.updateForm({ siteId, formId, formData, formFieldsData });
        },
        async addItem(item, type, index) {
            item._id = null;
            item.order = index;
            item.label = type;
            item.type = type;

            if (type === 'captcha') {
                item.fieldLabel = item.fieldLabel || "Güvenlik Doğrulaması";
                item.align = item.align || "center";
                item.placeholder = item.placeholder || "Yukarıdaki kodu girin";
                item.isRequired = item.isRequired !== undefined ? item.isRequired : true;
            }

            if (type === 'pagebreak') {
                item.fieldLabel = item.fieldLabel || "Sayfa Sonu";
                item.align = item.align || "center";
                item.nextButtonText = item.nextButtonText || "Devam";
                item.previousButtonText = item.previousButtonText || "Geri";
                item.showPreviousButton = item.showPreviousButton !== undefined ? item.showPreviousButton : true;
                item.showNextButton = item.showNextButton !== undefined ? item.showNextButton : true;
            }

            this.ensureSlugForField(item, this.formItems);

            this.formItems.splice(index, 0, item);
            this.formItems.forEach((item, idx) => {
                item.order = idx;
            });
            await this.saveForm();
            setTimeout(() => {
                this.loadForm();
            }, 200);
        },
        async removeItem(index) {
            if (this.formItems.length <= 1) {
                this.$toast.error("En Az 1 Alan Olmalıdır");
                return;
            }
            const [deleteFormField] = this.formItems.splice(index, 1);
            console.log("deleteFormField", deleteFormField);
            this.formItems.forEach((item, idx) => {
                item.order = idx;
            });
            await this.deleteFormField({ siteId: this.site._id, fieldId: deleteFormField._id });
            await this.saveForm();
        },
        setActiveTab(tab) {
            this.activeTab = tab;
        },
        openEditMenu() {
            this.openMenu = true;
        },
        closeEditMenu() {
            this.openMenu = false;
        },
        setActive(index) {
            this.activeIndex = index;
            this.activeItemId = this.formItems[index] && this.formItems[index]._id ? this.formItems[index]._id : null;
            this.openSettingsMenu = false;
        },
        openSettings(item, type) {
            if (!item || !item._id) {
                if (this.$toast && this.$toast.info) {
                    this.$toast.info("Alan oluşturuluyor, lütfen bekleyin…");
                }
                return;
            }
            this.settingsMenuType = type;
            this.settingsMenuItem = item;
            this.settingsMenuIndex = this.activeIndex;
            this.openSettingsMenu = true;
            console.log("Settings opened for:", {
                item,
                type,
            });
        },
        closeSettingsMenu() {
            this.openSettingsMenu = false;
            this.settingsMenuItem = null;
            this.settingsMenuType = "";
            this.settingsMenuIndex = null;
        },
        onDragEnter(event) {
            event.preventDefault();
            this.isDragging = true;
        },
        onDragLeave(event) {
            event.preventDefault();
            // Sadece container'dan çıkıldığında drag'i bitir
            if (!event.currentTarget.contains(event.relatedTarget)) {
                this.isDragging = false;
                this.dragOverIndex = -1;
            }
        },
        onDragOver(event) {
            event.preventDefault();
            if (!this.isDragging) {
                this.isDragging = true;
            }
            
            // Throttle the dragOverIndex updates to improve performance
            if (this.dragOverThrottle) {
                clearTimeout(this.dragOverThrottle);
            }
            
            this.dragOverThrottle = setTimeout(() => {
                // DOM elementlerinin hazır olduğunu kontrol et
                const formEditContainer = this.$el.querySelector(".form-edit");
                if (formEditContainer && formEditContainer.children.length > 0) {
                    this.dragOverIndex = this.getDropIndex(event);
                } else {
                    console.warn('⚠️ Form items not ready for drag over');
                }
            }, 16); // ~60fps
        },
        onDrop(event) {
            event.preventDefault();
            this.isDragging = false;
            this.dragOverIndex = -1;
            
            // Clear throttle timer
            if (this.dragOverThrottle) {
                clearTimeout(this.dragOverThrottle);
                this.dragOverThrottle = null;
            }
            
            this.$feature.beforeCreateFormField(() => {
                const item = JSON.parse(event.dataTransfer.getData("item"));
                const dropIndex = this.getDropIndex(event);
                this.addItem(item, item.id, dropIndex);
            }, this.formItems.length);
        },
        getDropIndex(event) {
            const formEditContainer = this.$el.querySelector(".form-edit");
            if (!formEditContainer) return this.formItems.length;

            const domItems = Array.from(formEditContainer.querySelectorAll(".form-item"));
            if (domItems.length === 0) return 0;

            // Görünür item'ların rect'lerini al (yükseklik 0 olanları atla)
            const rects = [];
            for (let i = 0; i < domItems.length; i++) {
                const r = domItems[i].getBoundingClientRect();
                if (r && r.height > 0) {
                    const idxAttr = domItems[i].getAttribute && domItems[i].getAttribute('data-index');
                    const realIndex = (idxAttr !== null && idxAttr !== undefined) ? parseInt(idxAttr, 10) : i;
                    rects.push({ rect: r, el: domItems[i], realIndex: isNaN(realIndex) ? i : realIndex });
                }
            }
            const n = rects.length;
            if (n === 0) return 0;

            const mouseY = event.clientY;

            // 1) Önce ilk ve son için sınırlar
            const firstMid = (rects[0].rect.top + rects[0].rect.bottom) / 2;
            if (mouseY < firstMid) return rects[0].realIndex;

            const lastMid = (rects[n - 1].rect.top + rects[n - 1].rect.bottom) / 2;
            if (mouseY >= lastMid) return rects[n - 1].realIndex + 1; // sonun altına bırak

            // 2) İki item arası orta noktaları hesapla ve uygun aralığı bul
            for (let i = 0; i < n - 1; i++) {
                const between = (rects[i].rect.bottom + rects[i + 1].rect.top) / 2;
                if (mouseY < between) {
                    // Bir sonraki gerçek index'ten önce ekle
                    return rects[i + 1].realIndex;
                }
            }

            return rects[n - 1].realIndex + 1;
        },
        async onEnd() {
            this.formItems.forEach((item, index) => {
                item.order = index;
            });
            if (this.activeItemId) {
                const newIndex = this.formItems.findIndex((it) => it && it._id === this.activeItemId);
                if (newIndex !== -1) {
                    this.activeIndex = newIndex;
                    if (this.openSettingsMenu) {
                        this.settingsMenuIndex = newIndex;
                        this.settingsMenuItem = this.formItems[newIndex];
                    }
                }
            }
            if (this.dragSaveTimer) {
                clearTimeout(this.dragSaveTimer);
            }
            this.dragSaveTimer = setTimeout(() => {
                this.saveForm();
            }, 300);
        },
        getItemKey(item) {
            return item && (item._id || item.slug || ('idx-' + item.order));
        },
        updateTitleSize(size) {
            if (this.settingsMenuItem) {
                if (!this.settingsMenuItem.title) this.$set(this.settingsMenuItem, 'title', {});
                this.$set(this.settingsMenuItem.title, 'titleSize', size);
                this.settingsMenuItem.titleSize = size;
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    this.$set(this.formItems[this.settingsMenuIndex], 'titleSize', size);
                }
            }
        },
        updateTextAlign(align) {
            if (this.settingsMenuItem) {
                if (!this.settingsMenuItem.title) this.$set(this.settingsMenuItem, 'title', {});
                this.$set(this.settingsMenuItem.title, 'titleAlign', align);
                this.settingsMenuItem.titleAlign = align;
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    this.$set(this.formItems[this.settingsMenuIndex], 'titleAlign', align);
                }
            }
        },
        updateTitleText(text) {
            if (this.settingsMenuItem) {
                if (!this.settingsMenuItem.title) this.$set(this.settingsMenuItem, 'title', {});
                this.$set(this.settingsMenuItem.title, 'titleText', text);
                this.settingsMenuItem.titleText = text;
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    this.$set(this.formItems[this.settingsMenuIndex], 'titleText', text);
                }
            }
        },
        updateSubTitleText(text) {
            if (this.settingsMenuItem) {
                if (!this.settingsMenuItem.title) this.$set(this.settingsMenuItem, 'title', {});
                this.$set(this.settingsMenuItem.title, 'subTitle', text);
                this.settingsMenuItem.subTitle = text;
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    this.$set(this.formItems[this.settingsMenuIndex], 'subTitle', text);
                }
            }
        },
        updateFieldLabel(label) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.fieldLabel = label;
                if (!this.settingsMenuItem.fullName) this.$set(this.settingsMenuItem, 'fullName', {});
                this.$set(this.settingsMenuItem.fullName, 'fieldLabel', label);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    this.$set(this.formItems[this.settingsMenuIndex], 'fieldLabel', label);
                    if (!this.formItems[this.settingsMenuIndex].fullName) this.$set(this.formItems[this.settingsMenuIndex], 'fullName', {});
                    this.$set(this.formItems[this.settingsMenuIndex].fullName, 'fieldLabel', label);
                }
            }
        },
        updateLabelAlign(align) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.fullNameAlign = align;
                if (!this.settingsMenuItem.fullName) this.$set(this.settingsMenuItem, 'fullName', {});
                this.$set(this.settingsMenuItem.fullName, 'fullNameAlign', align);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    this.$set(this.formItems[this.settingsMenuIndex], 'fullNameAlign', align);
                    if (!this.formItems[this.settingsMenuIndex].fullName) this.$set(this.formItems[this.settingsMenuIndex], 'fullName', {});
                    this.$set(this.formItems[this.settingsMenuIndex].fullName, 'fullNameAlign', align);
                }
            }
        },
        updateFirstName(name) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.firstName = name;
                if (!this.settingsMenuItem.fullName) this.$set(this.settingsMenuItem, 'fullName', {});
                this.$set(this.settingsMenuItem.fullName, 'firstName', name);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    this.$set(this.formItems[this.settingsMenuIndex], 'firstName', name);
                    if (!this.formItems[this.settingsMenuIndex].fullName) this.$set(this.formItems[this.settingsMenuIndex], 'fullName', {});
                    this.$set(this.formItems[this.settingsMenuIndex].fullName, 'firstName', name);
                }
            }
        },
        updateLastName(name) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.lastName = name;
                if (!this.settingsMenuItem.fullName) this.$set(this.settingsMenuItem, 'fullName', {});
                this.$set(this.settingsMenuItem.fullName, 'lastName', name);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    this.$set(this.formItems[this.settingsMenuIndex], 'lastName', name);
                    if (!this.formItems[this.settingsMenuIndex].fullName) this.$set(this.formItems[this.settingsMenuIndex], 'fullName', {});
                    this.$set(this.formItems[this.settingsMenuIndex].fullName, 'lastName', name);
                }
            }
        },
        updateFieldEmail(email) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.fieldEmail = email;
                if (!this.settingsMenuItem.email) this.$set(this.settingsMenuItem, 'email', {});
                this.$set(this.settingsMenuItem.email, 'fieldEmail', email);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    this.$set(this.formItems[this.settingsMenuIndex], 'fieldEmail', email);
                    if (!this.formItems[this.settingsMenuIndex].email) this.$set(this.formItems[this.settingsMenuIndex], 'email', {});
                    this.$set(this.formItems[this.settingsMenuIndex].email, 'fieldEmail', email);
                }
            }
        },
        updateLabelAlignMail(align) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.mailAlign = align;
                if (!this.settingsMenuItem.email) this.$set(this.settingsMenuItem, 'email', {});
                this.$set(this.settingsMenuItem.email, 'mailAlign', align);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    this.$set(this.formItems[this.settingsMenuIndex], 'mailAlign', align);
                    if (!this.formItems[this.settingsMenuIndex].email) this.$set(this.formItems[this.settingsMenuIndex], 'email', {});
                    this.$set(this.formItems[this.settingsMenuIndex].email, 'mailAlign', align);
                }
            }
        },
        updateAltLabelMail(label) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.altLabel = label;
                if (!this.settingsMenuItem.email) this.$set(this.settingsMenuItem, 'email', {});
                this.$set(this.settingsMenuItem.email, 'altLabel', label);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    this.$set(this.formItems[this.settingsMenuIndex], 'altLabel', label);
                    if (!this.formItems[this.settingsMenuIndex].email) this.$set(this.formItems[this.settingsMenuIndex], 'email', {});
                    this.$set(this.formItems[this.settingsMenuIndex].email, 'altLabel', label);
                }
            }
        },
        updateFieldLabelAddress(label) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.fieldAddress = label;
                if (!this.settingsMenuItem.address) this.$set(this.settingsMenuItem, 'address', {});
                this.$set(this.settingsMenuItem.address, 'fieldAddress', label);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].address) this.$set(this.formItems[this.settingsMenuIndex], 'address', {});
                    this.$set(this.formItems[this.settingsMenuIndex].address, 'fieldAddress', label);
                }
            }
        },
        updateLabelAlignAddress(align) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.align = align;
                if (!this.settingsMenuItem.address) this.$set(this.settingsMenuItem, 'address', {});
                this.$set(this.settingsMenuItem.address, 'align', align);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].address) this.$set(this.formItems[this.settingsMenuIndex], 'address', {});
                    this.$set(this.formItems[this.settingsMenuIndex].address, 'align', align);
                }
            }
        },
        updateFieldAddressLine1(address) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.addressLine1 = address;
                if (!this.settingsMenuItem.address) this.$set(this.settingsMenuItem, 'address', {});
                this.$set(this.settingsMenuItem.address, 'addressLine1', address);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].address) this.$set(this.formItems[this.settingsMenuIndex], 'address', {});
                    this.$set(this.formItems[this.settingsMenuIndex].address, 'addressLine1', address);
                }
            }
        },
        updateFieldAddressLine2(address) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.addressLine2 = address;
                if (!this.settingsMenuItem.address) this.$set(this.settingsMenuItem, 'address', {});
                this.$set(this.settingsMenuItem.address, 'addressLine2', address);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].address) this.$set(this.formItems[this.settingsMenuIndex], 'address', {});
                    this.$set(this.formItems[this.settingsMenuIndex].address, 'addressLine2', address);
                }
            }
        },
        updateFieldDistrict(district) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.district = district;
                if (!this.settingsMenuItem.address) this.$set(this.settingsMenuItem, 'address', {});
                this.$set(this.settingsMenuItem.address, 'district', district);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].address) this.$set(this.formItems[this.settingsMenuIndex], 'address', {});
                    this.$set(this.formItems[this.settingsMenuIndex].address, 'district', district);
                }
            }
        },
        updateFieldPostalCode(postalCode) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.postalCode = postalCode;
                if (!this.settingsMenuItem.address) this.$set(this.settingsMenuItem, 'address', {});
                this.$set(this.settingsMenuItem.address, 'postalCode', postalCode);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].address) this.$set(this.formItems[this.settingsMenuIndex], 'address', {});
                    this.$set(this.formItems[this.settingsMenuIndex].address, 'postalCode', postalCode);
                }
            }
        },
        updateFieldState(state) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.state = state;
                if (!this.settingsMenuItem.address) this.$set(this.settingsMenuItem, 'address', {});
                this.$set(this.settingsMenuItem.address, 'state', state);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].address) this.$set(this.formItems[this.settingsMenuIndex], 'address', {});
                    this.$set(this.formItems[this.settingsMenuIndex].address, 'state', state);
                }
            }
        },
        updateFieldPhone(phone) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.fieldPhone = phone;
                if (!this.settingsMenuItem.phone) this.$set(this.settingsMenuItem, 'phone', {});
                this.$set(this.settingsMenuItem.phone, 'fieldPhone', phone);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].phone) this.$set(this.formItems[this.settingsMenuIndex], 'phone', {});
                    this.$set(this.formItems[this.settingsMenuIndex].phone, 'fieldPhone', phone);
                }
            }
        },
        updateLabelAlignPhone(align) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.align = align;
                if (!this.settingsMenuItem.phone) this.$set(this.settingsMenuItem, 'phone', {});
                this.$set(this.settingsMenuItem.phone, 'align', align);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].phone) this.$set(this.formItems[this.settingsMenuIndex], 'phone', {});
                    this.$set(this.formItems[this.settingsMenuIndex].phone, 'align', align);
                }
            }
        },
        updateMaskedPhone(masked) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.masked = masked;
                if (!this.settingsMenuItem.phone) this.$set(this.settingsMenuItem, 'phone', {});
                this.$set(this.settingsMenuItem.phone, 'masked', masked);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].phone) this.$set(this.formItems[this.settingsMenuIndex], 'phone', {});
                    this.$set(this.formItems[this.settingsMenuIndex].phone, 'masked', masked);
                }
            }
        },
        updateFieldLabelDatePicker(label) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.fieldLabel = label;
                if (!this.settingsMenuItem.datePicker) this.$set(this.settingsMenuItem, 'datePicker', {});
                this.$set(this.settingsMenuItem.datePicker, 'fieldLabel', label);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].datePicker) this.$set(this.formItems[this.settingsMenuIndex], 'datePicker', {});
                    this.$set(this.formItems[this.settingsMenuIndex].datePicker, 'fieldLabel', label);
                }
            }
        },
        updateLabelAlignDatePicker(align) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.align = align;
                if (!this.settingsMenuItem.datePicker) this.$set(this.settingsMenuItem, 'datePicker', {});
                this.$set(this.settingsMenuItem.datePicker, 'align', align);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].datePicker) this.$set(this.formItems[this.settingsMenuIndex], 'datePicker', {});
                    this.$set(this.formItems[this.settingsMenuIndex].datePicker, 'align', align);
                }
            }
        },
        updateDateLabelDatePicker(label) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.dateLabel = label;
                if (!this.settingsMenuItem.datePicker) this.$set(this.settingsMenuItem, 'datePicker', {});
                this.$set(this.settingsMenuItem.datePicker, 'dateLabel', label);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].datePicker) this.$set(this.formItems[this.settingsMenuIndex], 'datePicker', {});
                    this.$set(this.formItems[this.settingsMenuIndex].datePicker, 'dateLabel', label);
                }
            }
        },
        updateFieldLabelShortText(label) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.fieldLabel = label;
                if (!this.settingsMenuItem.shortText) this.$set(this.settingsMenuItem, 'shortText', {});
                this.$set(this.settingsMenuItem.shortText, 'fieldLabel', label);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].shortText) this.$set(this.formItems[this.settingsMenuIndex], 'shortText', {});
                    this.$set(this.formItems[this.settingsMenuIndex].shortText, 'fieldLabel', label);
                }
            }
        },
        updateLabelAlignShortText(align) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.align = align;
                if (!this.settingsMenuItem.shortText) this.$set(this.settingsMenuItem, 'shortText', {});
                this.$set(this.settingsMenuItem.shortText, 'align', align);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].shortText) this.$set(this.formItems[this.settingsMenuIndex], 'shortText', {});
                    this.$set(this.formItems[this.settingsMenuIndex].shortText, 'align', align);
                }
            }
        },
        updateAltLabelShortText(label) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.altLabel = label;
                if (!this.settingsMenuItem.shortText) this.$set(this.settingsMenuItem, 'shortText', {});
                this.$set(this.settingsMenuItem.shortText, 'altLabel', label);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].shortText) this.$set(this.formItems[this.settingsMenuIndex], 'shortText', {});
                    this.$set(this.formItems[this.settingsMenuIndex].shortText, 'altLabel', label);
                }
            }
        },
        updateFieldLabelLongText(label) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.fieldLabel = label;
                if (!this.settingsMenuItem.longText) this.$set(this.settingsMenuItem, 'longText', {});
                this.$set(this.settingsMenuItem.longText, 'fieldLabel', label);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].longText) this.$set(this.formItems[this.settingsMenuIndex], 'longText', {});
                    this.$set(this.formItems[this.settingsMenuIndex].longText, 'fieldLabel', label);
                }
            }
        },
        updateLabelAlignLongText(align) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.align = align;
                if (!this.settingsMenuItem.longText) this.$set(this.settingsMenuItem, 'longText', {});
                this.$set(this.settingsMenuItem.longText, 'align', align);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].longText) this.$set(this.formItems[this.settingsMenuIndex], 'longText', {});
                    this.$set(this.formItems[this.settingsMenuIndex].longText, 'align', align);
                }
            }
        },
        updateAltLabelLongText(label) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.altLabel = label;
                if (!this.settingsMenuItem.longText) this.$set(this.settingsMenuItem, 'longText', {});
                this.$set(this.settingsMenuItem.longText, 'altLabel', label);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].longText) this.$set(this.formItems[this.settingsMenuIndex], 'longText', {});
                    this.$set(this.formItems[this.settingsMenuIndex].longText, 'altLabel', label);
                }
            }
        },
        updateMarginTopParagraph(marginTop) {
            if (this.settingsMenuItem) {
                if (!this.settingsMenuItem.paragraph) this.$set(this.settingsMenuItem, 'paragraph', {});
                this.$set(this.settingsMenuItem.paragraph, 'marginTop', marginTop);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].paragraph) this.$set(this.formItems[this.settingsMenuIndex], 'paragraph', {});
                    this.$set(this.formItems[this.settingsMenuIndex].paragraph, 'marginTop', marginTop);
                }
            }
        },
        updateMarginBottomParagraph(marginBottom) {
            if (this.settingsMenuItem) {
                if (!this.settingsMenuItem.paragraph) this.$set(this.settingsMenuItem, 'paragraph', {});
                this.$set(this.settingsMenuItem.paragraph, 'marginBottom', marginBottom);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].paragraph) this.$set(this.formItems[this.settingsMenuIndex], 'paragraph', {});
                    this.$set(this.formItems[this.settingsMenuIndex].paragraph, 'marginBottom', marginBottom);
                }
            }
        },
        updateSignatureFieldLabel(fieldLabel) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.fieldLabel = fieldLabel;
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    this.$set(this.formItems[this.settingsMenuIndex], 'fieldLabel', fieldLabel);
                }
            }
        },
        updateSignatureAlign(align) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.align = align;
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    this.$set(this.formItems[this.settingsMenuIndex], 'align', align);
                }
            }
        },
        updateSignatureAltLabel(altLabel) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.altLabel = altLabel;
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    this.$set(this.formItems[this.settingsMenuIndex], 'altLabel', altLabel);
                }
            }
        },
        updateSignatureIsRequired(isRequired) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.isRequired = isRequired;
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    this.$set(this.formItems[this.settingsMenuIndex], 'isRequired', isRequired);
                }
            }
        },
        updateSignatureCanvasWidth(canvasWidth) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.canvasWidth = canvasWidth;
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    this.$set(this.formItems[this.settingsMenuIndex], 'canvasWidth', canvasWidth);
                }
            }
        },
        updateSignatureCanvasHeight(canvasHeight) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.canvasHeight = canvasHeight;
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    this.$set(this.formItems[this.settingsMenuIndex], 'canvasHeight', canvasHeight);
                }
            }
        },
        updateSignatureStrokeColor(strokeColor) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.strokeColor = strokeColor;
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    this.$set(this.formItems[this.settingsMenuIndex], 'strokeColor', strokeColor);
                }
            }
        },
        updateSignatureStrokeWidth(strokeWidth) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.strokeWidth = strokeWidth;
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    this.$set(this.formItems[this.settingsMenuIndex], 'strokeWidth', strokeWidth);
                }
            }
        },
        updateSignatureBackgroundColor(backgroundColor) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.backgroundColor = backgroundColor;
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    this.$set(this.formItems[this.settingsMenuIndex], 'backgroundColor', backgroundColor);
                }
            }
        },
        updateSignatureBorderColor(borderColor) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.borderColor = borderColor;
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    this.$set(this.formItems[this.settingsMenuIndex], 'borderColor', borderColor);
                }
            }
        },
        updateSignatureBorderWidth(borderWidth) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.borderWidth = borderWidth;
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    this.$set(this.formItems[this.settingsMenuIndex], 'borderWidth', borderWidth);
                }
            }
        },
        updateSignatureBorderRadius(borderRadius) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.borderRadius = borderRadius;
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    this.$set(this.formItems[this.settingsMenuIndex], 'borderRadius', borderRadius);
                }
            }
        },
        updateSignatureShowClearButton(showClearButton) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.showClearButton = showClearButton;
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    this.$set(this.formItems[this.settingsMenuIndex], 'showClearButton', showClearButton);
                }
            }
        },
        updateSignatureClearButtonText(clearButtonText) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.clearButtonText = clearButtonText;
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    this.$set(this.formItems[this.settingsMenuIndex], 'clearButtonText', clearButtonText);
                }
            }
        },
        updateSignaturePlaceholder(placeholder) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.placeholder = placeholder;
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    this.$set(this.formItems[this.settingsMenuIndex], 'placeholder', placeholder);
                }
            }
        },
        updateCaptchaFieldLabel(fieldLabel) {
            console.log('🔴 FormBuilder updateCaptchaFieldLabel:', {
                fieldLabel,
                settingsMenuItem: this.settingsMenuItem,
                settingsMenuIndex: this.settingsMenuIndex
            });
            if (this.settingsMenuItem) {
                this.settingsMenuItem.fieldLabel = fieldLabel;
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    this.$set(this.formItems[this.settingsMenuIndex], 'fieldLabel', fieldLabel);
                    console.log('🔴 FormBuilder updated formItem fieldLabel:', this.formItems[this.settingsMenuIndex].fieldLabel);
                }
            }
        },
        updateCaptchaAlign(align) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.align = align;
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    this.$set(this.formItems[this.settingsMenuIndex], 'align', align);
                }
            }
        },
        updateCaptchaPlaceholder(placeholder) {
            console.log('🔴 FormBuilder updateCaptchaPlaceholder:', {
                placeholder,
                settingsMenuItem: this.settingsMenuItem,
                settingsMenuIndex: this.settingsMenuIndex
            });
            if (this.settingsMenuItem) {
                this.settingsMenuItem.placeholder = placeholder;
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    this.$set(this.formItems[this.settingsMenuIndex], 'placeholder', placeholder);
                    console.log('🔴 FormBuilder updated formItem placeholder:', this.formItems[this.settingsMenuIndex].placeholder);
                }
            }
        },
        updateCaptchaIsRequired(isRequired) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.isRequired = isRequired;
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    this.$set(this.formItems[this.settingsMenuIndex], 'isRequired', isRequired);
                }
            }
        },
        updatePagebreakAlign(align) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.align = align;
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    this.$set(this.formItems[this.settingsMenuIndex], 'align', align);
                }
            }
        },
        updatePagebreakNextButtonText(nextButtonText) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.nextButtonText = nextButtonText;
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    this.$set(this.formItems[this.settingsMenuIndex], 'nextButtonText', nextButtonText);
                }
            }
        },
        updatePagebreakPreviousButtonText(previousButtonText) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.previousButtonText = previousButtonText;
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    this.$set(this.formItems[this.settingsMenuIndex], 'previousButtonText', previousButtonText);
                }
            }
        },
        updatePagebreakShowPreviousButton(showPreviousButton) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.showPreviousButton = showPreviousButton;
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    this.$set(this.formItems[this.settingsMenuIndex], 'showPreviousButton', showPreviousButton);
                }
            }
        },
        updatePagebreakShowNextButton(showNextButton) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.showNextButton = showNextButton;
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    this.$set(this.formItems[this.settingsMenuIndex], 'showNextButton', showNextButton);
                }
            }
        },
        updateFieldLabelDropDownList(label) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.fieldLabel = label;
                if (!this.settingsMenuItem.dropDownList) this.$set(this.settingsMenuItem, 'dropDownList', {});
                this.$set(this.settingsMenuItem.dropDownList, 'fieldLabel', label);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].dropDownList) this.$set(this.formItems[this.settingsMenuIndex], 'dropDownList', {});
                    this.$set(this.formItems[this.settingsMenuIndex].dropDownList, 'fieldLabel', label);
                }
            }
        },
        updatePlaceholderDropDownList(placeholder) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.placeholder = placeholder;
                if (!this.settingsMenuItem.dropDownList) this.$set(this.settingsMenuItem, 'dropDownList', {});
                this.$set(this.settingsMenuItem.dropDownList, 'placeholder', placeholder);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].dropDownList) this.$set(this.formItems[this.settingsMenuIndex], 'dropDownList', {});
                    this.$set(this.formItems[this.settingsMenuIndex].dropDownList, 'placeholder', placeholder);
                }
            }
        },
        updateLabelAlignDropDownList(align) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.align = align;
                if (!this.settingsMenuItem.dropDownList) this.$set(this.settingsMenuItem, 'dropDownList', {});
                this.$set(this.settingsMenuItem.dropDownList, 'align', align);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].dropDownList) this.$set(this.formItems[this.settingsMenuIndex], 'dropDownList', {});
                    this.$set(this.formItems[this.settingsMenuIndex].dropDownList, 'align', align);
                }
            }
        },
        updateAltLabelDropDownList(label) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.altLabel = label;
                if (!this.settingsMenuItem.dropDownList) this.$set(this.settingsMenuItem, 'dropDownList', {});
                this.$set(this.settingsMenuItem.dropDownList, 'altLabel', label);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].dropDownList) this.$set(this.formItems[this.settingsMenuIndex], 'dropDownList', {});
                    this.$set(this.formItems[this.settingsMenuIndex].dropDownList, 'altLabel', label);
                }
            }
        },
        updateFieldLabelSingleChoice(label) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.fieldLabel = label;
                if (!this.settingsMenuItem.singleChoice) this.$set(this.settingsMenuItem, 'singleChoice', {});
                this.$set(this.settingsMenuItem.singleChoice, 'fieldLabel', label);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].singleChoice) this.$set(this.formItems[this.settingsMenuIndex], 'singleChoice', {});
                    this.$set(this.formItems[this.settingsMenuIndex].singleChoice, 'fieldLabel', label);
                }
            }
        },
        updateLabelAlignSingleChoice(align) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.align = align;
                if (!this.settingsMenuItem.singleChoice) this.$set(this.settingsMenuItem, 'singleChoice', {});
                this.$set(this.settingsMenuItem.singleChoice, 'align', align);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].singleChoice) this.$set(this.formItems[this.settingsMenuIndex], 'singleChoice', {});
                    this.$set(this.formItems[this.settingsMenuIndex].singleChoice, 'align', align);
                }
            }
        },
        updateFieldLabelMultipleSelection(label) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.fieldLabel = label;
                if (!this.settingsMenuItem.multipleSelection) this.$set(this.settingsMenuItem, 'multipleSelection', {});
                this.$set(this.settingsMenuItem.multipleSelection, 'fieldLabel', label);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].multipleSelection) this.$set(this.formItems[this.settingsMenuIndex], 'multipleSelection', {});
                    this.$set(this.formItems[this.settingsMenuIndex].multipleSelection, 'fieldLabel', label);
                }
            }
        },
        updateLabelAlignMultipleSelection(align) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.align = align;
                if (!this.settingsMenuItem.multipleSelection) this.$set(this.settingsMenuItem, 'multipleSelection', {});
                this.$set(this.settingsMenuItem.multipleSelection, 'align', align);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].multipleSelection) this.$set(this.formItems[this.settingsMenuIndex], 'multipleSelection', {});
                    this.$set(this.formItems[this.settingsMenuIndex].multipleSelection, 'align', align);
                }
            }
        },
        updateFieldLabelNumber(label) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.fieldLabel = label;
                if (!this.settingsMenuItem.number) this.$set(this.settingsMenuItem, 'number', {});
                this.$set(this.settingsMenuItem.number, 'fieldLabel', label);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].number) this.$set(this.formItems[this.settingsMenuIndex], 'number', {});
                    this.$set(this.formItems[this.settingsMenuIndex].number, 'fieldLabel', label);
                }
            }
        },
        updateLabelAlignNumber(align) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.labelAlign = align;
                if (!this.settingsMenuItem.number) this.$set(this.settingsMenuItem, 'number', {});
                this.$set(this.settingsMenuItem.number, 'labelAlign', align);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].number) this.$set(this.formItems[this.settingsMenuIndex], 'number', {});
                    this.$set(this.formItems[this.settingsMenuIndex].number, 'labelAlign', align);
                }
            }
        },
        updateSubLabelNumber(subLabel) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.subLabel = subLabel;
                if (!this.settingsMenuItem.number) this.$set(this.settingsMenuItem, 'number', {});
                this.$set(this.settingsMenuItem.number, 'subLabel', subLabel);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].number) this.$set(this.formItems[this.settingsMenuIndex], 'number', {});
                    this.$set(this.formItems[this.settingsMenuIndex].number, 'subLabel', subLabel);
                }
            }
        },
        updateFieldLabelHour(label) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.fieldLabel = label;
                if (!this.settingsMenuItem.hour) this.$set(this.settingsMenuItem, 'hour', {});
                this.$set(this.settingsMenuItem.hour, 'fieldLabel', label);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].hour) this.$set(this.formItems[this.settingsMenuIndex], 'hour', {});
                    this.$set(this.formItems[this.settingsMenuIndex].hour, 'fieldLabel', label);
                }
            }
        },
        updateLabelAlignHour(align) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.align = align;
                if (!this.settingsMenuItem.hour) this.$set(this.settingsMenuItem, 'hour', {});
                this.$set(this.settingsMenuItem.hour, 'align', align);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].hour) this.$set(this.formItems[this.settingsMenuIndex], 'hour', {});
                    this.$set(this.formItems[this.settingsMenuIndex].hour, 'align', align);
                }
            }
        },
        updateHourLabel(label) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.hourLabel = label;
                if (!this.settingsMenuItem.hour) this.$set(this.settingsMenuItem, 'hour', {});
                this.$set(this.settingsMenuItem.hour, 'hourLabel', label);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].hour) this.$set(this.formItems[this.settingsMenuIndex], 'hour', {});
                    this.$set(this.formItems[this.settingsMenuIndex].hour, 'hourLabel', label);
                }
            }
        },
        updateMinuteLabel(label) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.minuteLabel = label;
                if (!this.settingsMenuItem.hour) this.$set(this.settingsMenuItem, 'hour', {});
                this.$set(this.settingsMenuItem.hour, 'minuteLabel', label);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].hour) this.$set(this.formItems[this.settingsMenuIndex], 'hour', {});
                    this.$set(this.formItems[this.settingsMenuIndex].hour, 'minuteLabel', label);
                }
            }
        },
        updateFieldLabelSpinner(label) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.fieldLabel = label;
                if (!this.settingsMenuItem.spinner) this.$set(this.settingsMenuItem, 'spinner', {});
                this.$set(this.settingsMenuItem.spinner, 'fieldLabel', label);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].spinner) this.$set(this.formItems[this.settingsMenuIndex], 'spinner', {});
                    this.$set(this.formItems[this.settingsMenuIndex].spinner, 'fieldLabel', label);
                }
            }
        },
        updateLabelAlignSpinner(align) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.align = align;
                if (!this.settingsMenuItem.spinner) this.$set(this.settingsMenuItem, 'spinner', {});
                this.$set(this.settingsMenuItem.spinner, 'align', align);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].spinner) this.$set(this.formItems[this.settingsMenuIndex], 'spinner', {});
                    this.$set(this.formItems[this.settingsMenuIndex].spinner, 'align', align);
                }
            }
        },
        updateFieldLabelStarRating(label) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.fieldLabel = label;
                if (!this.settingsMenuItem.starRating) this.$set(this.settingsMenuItem, 'starRating', {});
                this.$set(this.settingsMenuItem.starRating, 'fieldLabel', label);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].starRating) this.$set(this.formItems[this.settingsMenuIndex], 'starRating', {});
                    this.$set(this.formItems[this.settingsMenuIndex].starRating, 'fieldLabel', label);
                }
            }
        },
        updateAlignmentStarRating(align) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.align = align;
                if (!this.settingsMenuItem.starRating) this.$set(this.settingsMenuItem, 'starRating', {});
                this.$set(this.settingsMenuItem.starRating, 'align', align);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].starRating) this.$set(this.formItems[this.settingsMenuIndex], 'starRating', {});
                    this.$set(this.formItems[this.settingsMenuIndex].starRating, 'align', align);
                }
            }
        },
        updateFieldLabelSend(label) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.fieldLabel = label;
                if (!this.settingsMenuItem.send) this.$set(this.settingsMenuItem, 'send', {});
                this.$set(this.settingsMenuItem.send, 'fieldLabel', label);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].send) this.$set(this.formItems[this.settingsMenuIndex], 'send', {});
                    this.$set(this.formItems[this.settingsMenuIndex].send, 'fieldLabel', label);
                }
            }
        },
        updateAlignmentSend(align) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.align = align;
                if (!this.settingsMenuItem.send) this.$set(this.settingsMenuItem, 'send', {});
                this.$set(this.settingsMenuItem.send, 'align', align);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].send) this.$set(this.formItems[this.settingsMenuIndex], 'send', {});
                    this.$set(this.formItems[this.settingsMenuIndex].send, 'align', align);
                }
            }
        },
        updateFieldLabelRatingScale(label) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.fieldLabel = label;
                if (!this.settingsMenuItem.ratingScale) this.$set(this.settingsMenuItem, 'ratingScale', {});
                this.$set(this.settingsMenuItem.ratingScale, 'fieldLabel', label);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].ratingScale) this.$set(this.formItems[this.settingsMenuIndex], 'ratingScale', {});
                    this.$set(this.formItems[this.settingsMenuIndex].ratingScale, 'fieldLabel', label);
                }
            }
        },
        updateAlignmentRatingScale(align) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.align = align;
                if (!this.settingsMenuItem.ratingScale) this.$set(this.settingsMenuItem, 'ratingScale', {});
                this.$set(this.settingsMenuItem.ratingScale, 'align', align);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].ratingScale) this.$set(this.formItems[this.settingsMenuIndex], 'ratingScale', {});
                    this.$set(this.formItems[this.settingsMenuIndex].ratingScale, 'align', align);
                }
            }
        },
        updateRatingScaleSubLabelOne(label) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.subLabelOne = label;
                if (!this.settingsMenuItem.ratingScale) this.$set(this.settingsMenuItem, 'ratingScale', {});
                this.$set(this.settingsMenuItem.ratingScale, 'subLabelOne', label);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].ratingScale) this.$set(this.formItems[this.settingsMenuIndex], 'ratingScale', {});
                    this.$set(this.formItems[this.settingsMenuIndex].ratingScale, 'subLabelOne', label);
                }
            }
        },
        updateRatingScaleSubLabelTwo(label) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.subLabelTwo = label;
                if (!this.settingsMenuItem.ratingScale) this.$set(this.settingsMenuItem, 'ratingScale', {});
                this.$set(this.settingsMenuItem.ratingScale, 'subLabelTwo', label);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].ratingScale) this.$set(this.formItems[this.settingsMenuIndex], 'ratingScale', {});
                    this.$set(this.formItems[this.settingsMenuIndex].ratingScale, 'subLabelTwo', label);
                }
            }
        },
        updateSeparationColor(color) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.separationColor = color;
                if (!this.settingsMenuItem.separationElement) this.$set(this.settingsMenuItem, 'separationElement', {});
                this.$set(this.settingsMenuItem.separationElement, 'separationColor', color);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].separationElement) this.$set(this.formItems[this.settingsMenuIndex], 'separationElement', {});
                    this.$set(this.formItems[this.settingsMenuIndex].separationElement, 'separationColor', color);
                }
            }
        },
        updateSeparatorStyle(style) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.separatorStyle = style;
                if (!this.settingsMenuItem.separationElement) this.$set(this.settingsMenuItem, 'separationElement', {});
                this.$set(this.settingsMenuItem.separationElement, 'separatorStyle', style);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].separationElement) this.$set(this.formItems[this.settingsMenuIndex], 'separationElement', {});
                    this.$set(this.formItems[this.settingsMenuIndex].separationElement, 'separatorStyle', style);
                }
            }
        },
        updateSeparatorHeight(height) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.separatorHeight = height;
                if (!this.settingsMenuItem.separationElement) this.$set(this.settingsMenuItem, 'separationElement', {});
                this.$set(this.settingsMenuItem.separationElement, 'separatorHeight', height);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].separationElement) this.$set(this.formItems[this.settingsMenuIndex], 'separationElement', {});
                    this.$set(this.formItems[this.settingsMenuIndex].separationElement, 'separatorHeight', height);
                }
            }
        },
        updateSeparatorBottom(bottom) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.separatorBottom = bottom;
                if (!this.settingsMenuItem.separationElement) this.$set(this.settingsMenuItem, 'separationElement', {});
                this.$set(this.settingsMenuItem.separationElement, 'separatorBottom', bottom);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].separationElement) this.$set(this.formItems[this.settingsMenuIndex], 'separationElement', {});
                    this.$set(this.formItems[this.settingsMenuIndex].separationElement, 'separatorBottom', bottom);
                }
            }
        },
        updateSeparatorTop(top) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.separatorTop = top;
                if (!this.settingsMenuItem.separationElement) this.$set(this.settingsMenuItem, 'separationElement', {});
                this.$set(this.settingsMenuItem.separationElement, 'separatorTop', top);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].separationElement) this.$set(this.formItems[this.settingsMenuIndex], 'separationElement', {});
                    this.$set(this.formItems[this.settingsMenuIndex].separationElement, 'separatorTop', top);
                }
            }
        },
        updateSeparatorSide(side) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.separatorSide = side;
                if (!this.settingsMenuItem.separationElement) this.$set(this.settingsMenuItem, 'separationElement', {});
                this.$set(this.settingsMenuItem.separationElement, 'separatorSide', side);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].separationElement) this.$set(this.formItems[this.settingsMenuIndex], 'separationElement', {});
                    this.$set(this.formItems[this.settingsMenuIndex].separationElement, 'separatorSide', side);
                }
            }
        },
        updateFileLabel(label) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.fieldLabelFile = label;
                if (!this.settingsMenuItem.fileUpload) this.$set(this.settingsMenuItem, 'fileUpload', {});
                this.$set(this.settingsMenuItem.fileUpload, 'fieldLabelFile', label);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].fileUpload) this.$set(this.formItems[this.settingsMenuIndex], 'fileUpload', {});
                    this.$set(this.formItems[this.settingsMenuIndex].fileUpload, 'fieldLabelFile', label);
                }
            }
        },
        updateFileAltLabelOne(label) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.altLabelOne = label;
                if (!this.settingsMenuItem.fileUpload) this.$set(this.settingsMenuItem, 'fileUpload', {});
                this.$set(this.settingsMenuItem.fileUpload, 'altLabelOne', label);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].fileUpload) this.$set(this.formItems[this.settingsMenuIndex], 'fileUpload', {});
                    this.$set(this.formItems[this.settingsMenuIndex].fileUpload, 'altLabelOne', label);
                }
            }
        },
        updateFileAltLabelTwo(label) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.altLabelTwo = label;
                if (!this.settingsMenuItem.fileUpload) this.$set(this.settingsMenuItem, 'fileUpload', {});
                this.$set(this.settingsMenuItem.fileUpload, 'altLabelTwo', label);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].fileUpload) this.$set(this.formItems[this.settingsMenuIndex], 'fileUpload', {});
                    this.$set(this.formItems[this.settingsMenuIndex].fileUpload, 'altLabelTwo', label);
                }
            }
        },
        updateFileAlign(align) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.align = align;
                if (!this.settingsMenuItem.fileUpload) this.$set(this.settingsMenuItem, 'fileUpload', {});
                this.$set(this.settingsMenuItem.fileUpload, 'align', align);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].fileUpload) this.$set(this.formItems[this.settingsMenuIndex], 'fileUpload', {});
                    this.$set(this.formItems[this.settingsMenuIndex].fileUpload, 'align', align);
                }
            }
        },
        updateRequired(required) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.isRequired = required;
                if (this.settingsMenuType === 'fullName') {
                    if (!this.settingsMenuItem.fullName) this.$set(this.settingsMenuItem, 'fullName', {});
                    this.$set(this.settingsMenuItem.fullName, 'isRequired', required);
                } else if (this.settingsMenuType === 'email') {
                    if (!this.settingsMenuItem.email) this.$set(this.settingsMenuItem, 'email', {});
                    this.$set(this.settingsMenuItem.email, 'isRequired', required);
                } else if (this.settingsMenuType === 'phone') {
                    if (!this.settingsMenuItem.phone) this.$set(this.settingsMenuItem, 'phone', {});
                    this.$set(this.settingsMenuItem.phone, 'isRequired', required);
                } else if (this.settingsMenuType === 'address') {
                    if (!this.settingsMenuItem.address) this.$set(this.settingsMenuItem, 'address', {});
                    this.$set(this.settingsMenuItem.address, 'isRequired', required);
                } else if (this.settingsMenuType === 'datePicker') {
                    if (!this.settingsMenuItem.datePicker) this.$set(this.settingsMenuItem, 'datePicker', {});
                    this.$set(this.settingsMenuItem.datePicker, 'isRequired', required);
                } else if (this.settingsMenuType === 'shortText') {
                    if (!this.settingsMenuItem.shortText) this.$set(this.settingsMenuItem, 'shortText', {});
                    this.$set(this.settingsMenuItem.shortText, 'isRequired', required);
                } else if (this.settingsMenuType === 'longText') {
                    if (!this.settingsMenuItem.longText) this.$set(this.settingsMenuItem, 'longText', {});
                    this.$set(this.settingsMenuItem.longText, 'isRequired', required);
                } else if (this.settingsMenuType === 'dropDownList') {
                    if (!this.settingsMenuItem.dropDownList) this.$set(this.settingsMenuItem, 'dropDownList', {});
                    this.$set(this.settingsMenuItem.dropDownList, 'isRequired', required);
                } else if (this.settingsMenuType === 'singleChoice') {
                    if (!this.settingsMenuItem.singleChoice) this.$set(this.settingsMenuItem, 'singleChoice', {});
                    this.$set(this.settingsMenuItem.singleChoice, 'isRequired', required);
                } else if (this.settingsMenuType === 'multipleSelection') {
                    if (!this.settingsMenuItem.multipleSelection) this.$set(this.settingsMenuItem, 'multipleSelection', {});
                    this.$set(this.settingsMenuItem.multipleSelection, 'isRequired', required);
                } else if (this.settingsMenuType === 'fileUpload') {
                    if (!this.settingsMenuItem.fileUpload) this.$set(this.settingsMenuItem, 'fileUpload', {});
                    this.$set(this.settingsMenuItem.fileUpload, 'isRequired', required);
                } else if (this.settingsMenuType === 'hour') {
                    if (!this.settingsMenuItem.hour) this.$set(this.settingsMenuItem, 'hour', {});
                    this.$set(this.settingsMenuItem.hour, 'isRequired', required);
                } else if (this.settingsMenuType === 'spinner') {
                    if (!this.settingsMenuItem.spinner) this.$set(this.settingsMenuItem, 'spinner', {});
                    this.$set(this.settingsMenuItem.spinner, 'isRequired', required);
                }
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    this.$set(this.formItems[this.settingsMenuIndex], 'isRequired', required);
                    if (this.settingsMenuType === 'fullName') {
                        if (!this.formItems[this.settingsMenuIndex].fullName) this.$set(this.formItems[this.settingsMenuIndex], 'fullName', {});
                        this.$set(this.formItems[this.settingsMenuIndex].fullName, 'isRequired', required);
                    } else if (this.settingsMenuType === 'email') {
                        if (!this.formItems[this.settingsMenuIndex].email) this.$set(this.formItems[this.settingsMenuIndex], 'email', {});
                        this.$set(this.formItems[this.settingsMenuIndex].email, 'isRequired', required);
                    } else if (this.settingsMenuType === 'phone') {
                        if (!this.formItems[this.settingsMenuIndex].phone) this.$set(this.formItems[this.settingsMenuIndex], 'phone', {});
                        this.$set(this.formItems[this.settingsMenuIndex].phone, 'isRequired', required);
                    } else if (this.settingsMenuType === 'address') {
                        if (!this.formItems[this.settingsMenuIndex].address) this.$set(this.formItems[this.settingsMenuIndex], 'address', {});
                        this.$set(this.formItems[this.settingsMenuIndex].address, 'isRequired', required);
                    } else if (this.settingsMenuType === 'datePicker') {
                        if (!this.formItems[this.settingsMenuIndex].datePicker) this.$set(this.formItems[this.settingsMenuIndex], 'datePicker', {});
                        this.$set(this.formItems[this.settingsMenuIndex].datePicker, 'isRequired', required);
                    } else if (this.settingsMenuType === 'shortText') {
                        if (!this.formItems[this.settingsMenuIndex].shortText) this.$set(this.formItems[this.settingsMenuIndex], 'shortText', {});
                        this.$set(this.formItems[this.settingsMenuIndex].shortText, 'isRequired', required);
                    } else if (this.settingsMenuType === 'longText') {
                        if (!this.formItems[this.settingsMenuIndex].longText) this.$set(this.formItems[this.settingsMenuIndex], 'longText', {});
                        this.$set(this.formItems[this.settingsMenuIndex].longText, 'isRequired', required);
                    } else if (this.settingsMenuType === 'dropDownList') {
                        if (!this.formItems[this.settingsMenuIndex].dropDownList) this.$set(this.formItems[this.settingsMenuIndex], 'dropDownList', {});
                        this.$set(this.formItems[this.settingsMenuIndex].dropDownList, 'isRequired', required);
                    } else if (this.settingsMenuType === 'fileUpload') {
                        if (!this.formItems[this.settingsMenuIndex].fileUpload) this.$set(this.formItems[this.settingsMenuIndex], 'fileUpload', {});
                        this.$set(this.formItems[this.settingsMenuIndex].fileUpload, 'isRequired', required);
                    } else if (this.settingsMenuType === 'hour') {
                        if (!this.formItems[this.settingsMenuIndex].hour) this.$set(this.formItems[this.settingsMenuIndex], 'hour', {});
                        this.$set(this.formItems[this.settingsMenuIndex].hour, 'isRequired', required);
                    } else if (this.settingsMenuType === 'spinner') {
                        if (!this.formItems[this.settingsMenuIndex].spinner) this.$set(this.formItems[this.settingsMenuIndex], 'spinner', {});
                        this.$set(this.formItems[this.settingsMenuIndex].spinner, 'isRequired', required);
                    } else if (this.settingsMenuType === 'number') {
                        if (!this.formItems[this.settingsMenuIndex].number) this.$set(this.formItems[this.settingsMenuIndex], 'number', {});
                        this.$set(this.formItems[this.settingsMenuIndex].number, 'isRequired', required);
                    } else if (this.settingsMenuType === 'singleChoice') {
                        if (!this.formItems[this.settingsMenuIndex].singleChoice) this.$set(this.formItems[this.settingsMenuIndex], 'singleChoice', {});
                        this.$set(this.formItems[this.settingsMenuIndex].singleChoice, 'isRequired', required);
                    } else if (this.settingsMenuType === 'multipleSelection') {
                        if (!this.formItems[this.settingsMenuIndex].multipleSelection) this.$set(this.formItems[this.settingsMenuIndex], 'multipleSelection', {});
                        this.$set(this.formItems[this.settingsMenuIndex].multipleSelection, 'isRequired', required);
                    }
                }
            }
        },
        updateVisibility(visibility) {
            if (this.settingsMenuItem) {
                this.$set(this.settingsMenuItem, 'visibility', visibility);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    this.$set(this.formItems[this.settingsMenuIndex], 'visibility', visibility);
                }
            }
        },
        updateSlug(slug) {
            if (this.settingsMenuItem) {
                this.$set(this.settingsMenuItem, 'slug', slug);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    this.$set(this.formItems[this.settingsMenuIndex], 'slug', slug);
                }
            }
        },
        updateImageUrl(url) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.asset = url;
                if (!this.settingsMenuItem.image) this.$set(this.settingsMenuItem, 'image', {});
                this.$set(this.settingsMenuItem.image, 'asset', url);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].image) this.$set(this.formItems[this.settingsMenuIndex], 'image', {});
                    this.$set(this.formItems[this.settingsMenuIndex].image, 'asset', url);
                }
            }
        },
        updateImageWidth(width) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.imageWidth = width;
                if (!this.settingsMenuItem.image) this.$set(this.settingsMenuItem, 'image', {});
                this.$set(this.settingsMenuItem.image, 'width', width);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].image) this.$set(this.formItems[this.settingsMenuIndex], 'image', {});
                    this.$set(this.formItems[this.settingsMenuIndex].image, 'width', width);
                }
            }
        },
        updateImageHeight(height) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.imageHeight = height;
                if (!this.settingsMenuItem.image) this.$set(this.settingsMenuItem, 'image', {});
                this.$set(this.settingsMenuItem.image, 'height', height);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].image) this.$set(this.formItems[this.settingsMenuIndex], 'image', {});
                    this.$set(this.formItems[this.settingsMenuIndex].image, 'height', height);
                }
            }
        },
        updateImageAlign(align) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.align = align;
                if (!this.settingsMenuItem.image) this.$set(this.settingsMenuItem, 'image', {});
                this.$set(this.settingsMenuItem.image, 'align', align);
                if (typeof this.settingsMenuIndex === 'number' && this.formItems[this.settingsMenuIndex]) {
                    if (!this.formItems[this.settingsMenuIndex].image) this.$set(this.formItems[this.settingsMenuIndex], 'image', {});
                    this.$set(this.formItems[this.settingsMenuIndex].image, 'align', align);
                }
            }
        },
        beforeUpload(file) {
            const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/jpg" || file.type === "image/png";
            if (!isJpgOrPng) {
                this.$toasted.show("Sadece Resim Yükleyebilirsiniz!", {
                    duration: 3000,
                });
            }
            const isLt5M = file.size / 1024 / 1024 < 50;
            if (!isLt5M) {
                this.$toasted.show("Resminizin Boyutu En Fazla 5MB Olmalıdır!", {
                    duration: 3000,
                });
            }
            return isJpgOrPng && isLt5M;
        },
        updateField(updatedFields) {
            const activeItem = this.formItems[this.activeIndex];
            if (!activeItem) return;
            this.applyLocalUpdate(activeItem, updatedFields);
            if (this.settingsMenuItem && this.settingsMenuIndex === this.activeIndex) {
                this.applyLocalUpdate(this.settingsMenuItem, updatedFields);
            }
        },
        updateFieldFrom(itemId, updatedFields) {
            const index = this.formItems.findIndex(function (it) { return it._id === itemId; });
            if (index === -1) return;
            this.applyLocalUpdate(this.formItems[index], updatedFields);
            if (this.settingsMenuItem && this.settingsMenuIndex === index) {
                this.applyLocalUpdate(this.settingsMenuItem, updatedFields);
            }
        },
        applyLocalUpdate(targetItem, updated) {
            if (!updated || !targetItem) return;
            if (typeof updated.label === 'string') {
                if (targetItem.type === 'title') {
                    // Title için label metni item.titleText üzerinden yönetiliyor; burada değişiklik yok
                } else if (targetItem.type === 'fileUpload') {
                    targetItem.fieldLabelFile = updated.label;
                } else {
                    targetItem.fieldLabel = updated.label;
                }
            }
            const keys = Object.keys(updated).filter(function (k) { return k !== 'label'; });
            for (let i = 0; i < keys.length; i++) {
                const k = keys[i];
                const data = updated[k] || {};
                switch (k) {
                    case 'fullName':
                        targetItem.fieldLabel = data.fieldLabel || targetItem.fieldLabel;
                        targetItem.fullNameAlign = data.fullNameAlign || targetItem.fullNameAlign;
                        targetItem.firstName = (data.firstName !== undefined) ? data.firstName : targetItem.firstName;
                        targetItem.lastName = (data.lastName !== undefined) ? data.lastName : targetItem.lastName;
                        if (typeof data.isRequired === 'boolean') targetItem.isRequired = data.isRequired;
                        if (!targetItem.fullName) this.$set(targetItem, 'fullName', {});
                        if (data.fieldLabel !== undefined) this.$set(targetItem.fullName, 'fieldLabel', data.fieldLabel);
                        if (data.fullNameAlign !== undefined) this.$set(targetItem.fullName, 'fullNameAlign', data.fullNameAlign);
                        if (data.firstName !== undefined) this.$set(targetItem.fullName, 'firstName', data.firstName);
                        if (data.lastName !== undefined) this.$set(targetItem.fullName, 'lastName', data.lastName);
                        if (data.isRequired !== undefined) this.$set(targetItem.fullName, 'isRequired', data.isRequired);
                        break;
                    case 'visibility':
                        if (updated.visibility !== undefined) {
                            this.$set(targetItem, 'visibility', updated.visibility);
                        }
                        break;
                    case 'slug':
                        if (updated.slug !== undefined) {
                            this.$set(targetItem, 'slug', updated.slug);
                        }
                        break;
                    case 'email':
                        targetItem.fieldEmail = data.fieldEmail || targetItem.fieldEmail;
                        targetItem.mailAlign = data.mailAlign || targetItem.mailAlign;
                        targetItem.altLabel = (data.altLabel !== undefined) ? data.altLabel : targetItem.altLabel;
                        if (typeof data.isRequired === 'boolean') targetItem.isRequired = data.isRequired;
                        if (!targetItem.email) this.$set(targetItem, 'email', {});
                        if (data.fieldEmail !== undefined) this.$set(targetItem.email, 'fieldEmail', data.fieldEmail);
                        if (data.mailAlign !== undefined) this.$set(targetItem.email, 'mailAlign', data.mailAlign);
                        if (data.altLabel !== undefined) this.$set(targetItem.email, 'altLabel', data.altLabel);
                        if (data.isRequired !== undefined) this.$set(targetItem.email, 'isRequired', data.isRequired);
                        break;
                    case 'address':
                        targetItem.fieldAddress = data.fieldAddress || targetItem.fieldAddress;
                        targetItem.align = data.align || targetItem.align;
                        targetItem.addressLine1 = data.addressLine1 || targetItem.addressLine1;
                        targetItem.addressLine2 = data.addressLine2 || targetItem.addressLine2;
                        targetItem.district = data.district || targetItem.district;
                        targetItem.postalCode = data.postalCode || targetItem.postalCode;
                        targetItem.state = data.state || targetItem.state;
                        if (typeof data.isRequired === 'boolean') targetItem.isRequired = data.isRequired;
                        if (!targetItem.address) targetItem.address = {};
                        if (data.fieldAddress !== undefined) targetItem.address.fieldAddress = data.fieldAddress;
                        if (data.align !== undefined) targetItem.address.align = data.align;
                        if (data.addressLine1 !== undefined) targetItem.address.addressLine1 = data.addressLine1;
                        if (data.addressLine2 !== undefined) targetItem.address.addressLine2 = data.addressLine2;
                        if (data.district !== undefined) targetItem.address.district = data.district;
                        if (data.postalCode !== undefined) targetItem.address.postalCode = data.postalCode;
                        if (data.state !== undefined) targetItem.address.state = data.state;
                        if (data.isRequired !== undefined) targetItem.address.isRequired = data.isRequired;
                        break;
                    case 'phone':
                        targetItem.fieldPhone = data.fieldPhone || targetItem.fieldPhone;
                        targetItem.align = data.align || targetItem.align;
                        targetItem.masked = (data.masked !== undefined) ? data.masked : targetItem.masked;
                        if (typeof data.isRequired === 'boolean') targetItem.isRequired = data.isRequired;
                        if (!targetItem.phone) targetItem.phone = {};
                        if (data.fieldPhone !== undefined) targetItem.phone.fieldPhone = data.fieldPhone;
                        if (data.align !== undefined) targetItem.phone.align = data.align;
                        if (data.masked !== undefined) targetItem.phone.masked = data.masked;
                        if (data.isRequired !== undefined) targetItem.phone.isRequired = data.isRequired;
                        break;
                    case 'datePicker':
                        targetItem.fieldLabel = data.fieldLabel || targetItem.fieldLabel;
                        targetItem.align = data.align || targetItem.align;
                        targetItem.dateLabel = data.dateLabel || targetItem.dateLabel;
                        if (typeof data.isRequired === 'boolean') targetItem.isRequired = data.isRequired;
                        if (!targetItem.datePicker) targetItem.datePicker = {};
                        if (data.fieldLabel !== undefined) targetItem.datePicker.fieldLabel = data.fieldLabel;
                        if (data.align !== undefined) targetItem.datePicker.align = data.align;
                        if (data.dateLabel !== undefined) targetItem.datePicker.dateLabel = data.dateLabel;
                        if (data.isRequired !== undefined) targetItem.datePicker.isRequired = data.isRequired;
                        break;
                    case 'shortText':
                        targetItem.fieldLabel = data.fieldLabel || targetItem.fieldLabel;
                        targetItem.align = data.align || targetItem.align;
                        targetItem.altLabel = (data.altLabel !== undefined) ? data.altLabel : targetItem.altLabel;
                        if (typeof data.isRequired === 'boolean') targetItem.isRequired = data.isRequired;
                        if (!targetItem.shortText) targetItem.shortText = {};
                        if (data.fieldLabel !== undefined) targetItem.shortText.fieldLabel = data.fieldLabel;
                        if (data.align !== undefined) targetItem.shortText.align = data.align;
                        if (data.altLabel !== undefined) targetItem.shortText.altLabel = data.altLabel;
                        if (data.isRequired !== undefined) targetItem.shortText.isRequired = data.isRequired;
                        break;
                    case 'longText':
                        targetItem.fieldLabel = data.fieldLabel || targetItem.fieldLabel;
                        targetItem.align = data.align || targetItem.align;
                        targetItem.altLabel = (data.altLabel !== undefined) ? data.altLabel : targetItem.altLabel;
                        if (typeof data.isRequired === 'boolean') targetItem.isRequired = data.isRequired;
                        if (!targetItem.longText) targetItem.longText = {};
                        if (data.fieldLabel !== undefined) targetItem.longText.fieldLabel = data.fieldLabel;
                        if (data.align !== undefined) targetItem.longText.align = data.align;
                        if (data.altLabel !== undefined) targetItem.longText.altLabel = data.altLabel;
                        if (data.isRequired !== undefined) targetItem.longText.isRequired = data.isRequired;
                        break;
                    case 'dropDownList':
                        targetItem.fieldLabel = data.fieldLabel || targetItem.fieldLabel;
                        targetItem.align = data.align || targetItem.align;
                        targetItem.altLabel = (data.altLabel !== undefined) ? data.altLabel : targetItem.altLabel;
                        if (data.items !== undefined) targetItem.items = data.items;
                        if (typeof data.isRequired === 'boolean') targetItem.isRequired = data.isRequired;
                        if (data.placeholder !== undefined) targetItem.placeholder = data.placeholder;
                        if (!targetItem.dropDownList) targetItem.dropDownList = {};
                        if (data.fieldLabel !== undefined) targetItem.dropDownList.fieldLabel = data.fieldLabel;
                        if (data.align !== undefined) targetItem.dropDownList.align = data.align;
                        if (data.altLabel !== undefined) targetItem.dropDownList.altLabel = data.altLabel;
                        if (data.isRequired !== undefined) targetItem.dropDownList.isRequired = data.isRequired;
                        if (data.items !== undefined) targetItem.dropDownList.items = data.items;
                        if (data.placeholder !== undefined) targetItem.dropDownList.placeholder = data.placeholder;
                        break;
                    case 'singleChoice':
                        targetItem.fieldLabel = data.fieldLabel || targetItem.fieldLabel;
                        targetItem.align = data.align || targetItem.align;
                        if (data.items !== undefined) targetItem.items = data.items;
                        if (typeof data.isRequired === 'boolean') targetItem.isRequired = data.isRequired;
                        if (data.otherOption !== undefined) targetItem.otherOption = data.otherOption;
                        if (data.defaultValues !== undefined) targetItem.defaultValues = (Array.isArray(data.defaultValues)) ? data.defaultValues : [];
                        if (!targetItem.singleChoice) targetItem.singleChoice = {};
                        if (data.fieldLabel !== undefined) targetItem.singleChoice.fieldLabel = data.fieldLabel;
                        if (data.align !== undefined) targetItem.singleChoice.align = data.align;
                        if (data.items !== undefined) targetItem.singleChoice.items = data.items;
                        if (data.isRequired !== undefined) targetItem.singleChoice.isRequired = data.isRequired;
                        if (data.otherOption !== undefined) targetItem.singleChoice.otherOption = data.otherOption;
                        if (data.defaultValues !== undefined) targetItem.singleChoice.defaultValues = (Array.isArray(data.defaultValues)) ? data.defaultValues : [];
                        break;
                    case 'multipleSelection':
                        targetItem.fieldLabel = data.fieldLabel || targetItem.fieldLabel;
                        if (data.items !== undefined) targetItem.items = data.items;
                        targetItem.align = data.align || targetItem.align;
                        if (typeof data.isRequired === 'boolean') targetItem.isRequired = data.isRequired;
                        if (data.otherOption !== undefined) targetItem.otherOption = data.otherOption;
                        if (data.defaultValues !== undefined) targetItem.defaultValues = (Array.isArray(data.defaultValues)) ? data.defaultValues : [];
                        if (data.visibility !== undefined) targetItem.visibility = data.visibility;
                        if (!targetItem.multipleSelection) targetItem.multipleSelection = {};
                        if (data.fieldLabel !== undefined) targetItem.multipleSelection.fieldLabel = data.fieldLabel;
                        if (data.items !== undefined) targetItem.multipleSelection.items = data.items;
                        if (data.align !== undefined) targetItem.multipleSelection.align = data.align;
                        if (data.isRequired !== undefined) targetItem.multipleSelection.isRequired = data.isRequired;
                        if (data.otherOption !== undefined) targetItem.multipleSelection.otherOption = data.otherOption;
                        if (data.defaultValues !== undefined) targetItem.multipleSelection.defaultValues = (Array.isArray(data.defaultValues)) ? data.defaultValues : [];
                        if (data.visibility !== undefined) targetItem.multipleSelection.visibility = data.visibility;
                        break;
                    case 'number':
                        targetItem.fieldLabel = data.fieldLabel || targetItem.fieldLabel;
                        targetItem.labelAlign = data.labelAlign || targetItem.labelAlign;
                        targetItem.subLabel = (data.subLabel !== undefined && data.subLabel !== null) ? data.subLabel : targetItem.subLabel;
                        if (typeof data.isRequired === 'boolean') targetItem.isRequired = data.isRequired;
                        if (data.slug !== undefined) targetItem.slug = data.slug;
                        if (!targetItem.number) targetItem.number = {};
                        if (data.fieldLabel !== undefined) targetItem.number.fieldLabel = data.fieldLabel;
                        if (data.labelAlign !== undefined) targetItem.number.labelAlign = data.labelAlign;
                        if (data.subLabel !== undefined) targetItem.number.subLabel = data.subLabel;
                        if (data.isRequired !== undefined) targetItem.number.isRequired = data.isRequired;
                        if (data.number !== undefined) targetItem.number.number = data.number;
                        if (data.slug !== undefined) targetItem.number.slug = data.slug;
                        break;
                    case 'hour':
                        targetItem.fieldLabel = data.fieldLabel || targetItem.fieldLabel;
                        targetItem.align = data.align || targetItem.align;
                        targetItem.hourLabel = data.hourLabel || targetItem.hourLabel;
                        targetItem.minuteLabel = data.minuteLabel || targetItem.minuteLabel;
                        if (data.time !== undefined) targetItem.time = data.time;
                        if (data.period !== undefined) targetItem.period = data.period;
                        if (data.visibility !== undefined) targetItem.visibility = data.visibility;
                        if (typeof data.isRequired === 'boolean') targetItem.isRequired = data.isRequired;
                        if (!targetItem.hour) targetItem.hour = {};
                        if (data.fieldLabel !== undefined) targetItem.hour.fieldLabel = data.fieldLabel;
                        if (data.align !== undefined) targetItem.hour.align = data.align;
                        if (data.hourLabel !== undefined) targetItem.hour.hourLabel = data.hourLabel;
                        if (data.minuteLabel !== undefined) targetItem.hour.minuteLabel = data.minuteLabel;
                        if (data.time !== undefined) targetItem.hour.time = data.time;
                        if (data.period !== undefined) targetItem.hour.period = data.period;
                        if (data.isRequired !== undefined) targetItem.hour.isRequired = data.isRequired;
                        if (data.visibility !== undefined) targetItem.hour.visibility = data.visibility;
                        break;
                    case 'spinner':
                        targetItem.fieldLabel = data.fieldLabel || targetItem.fieldLabel;
                        targetItem.align = data.align || targetItem.align;
                        if (typeof data.isRequired === 'boolean') targetItem.isRequired = data.isRequired;
                        if (data.visibility !== undefined) targetItem.visibility = data.visibility;
                        if (data.value !== undefined) targetItem.value = data.value;
                        if (!targetItem.spinner) targetItem.spinner = {};
                        if (data.fieldLabel !== undefined) targetItem.spinner.fieldLabel = data.fieldLabel;
                        if (data.align !== undefined) targetItem.spinner.align = data.align;
                        if (data.isRequired !== undefined) targetItem.spinner.isRequired = data.isRequired;
                        if (data.visibility !== undefined) targetItem.spinner.visibility = data.visibility;
                        if (data.value !== undefined) targetItem.spinner.value = data.value;
                        break;
                    case 'starRating':
                        targetItem.fieldLabel = data.fieldLabel || targetItem.fieldLabel;
                        targetItem.align = data.align || targetItem.align;
                        if (data.rating !== undefined) targetItem.rating = data.rating;
                        if (!targetItem.starRating) this.$set(targetItem, 'starRating', {});
                        if (data.fieldLabel !== undefined) this.$set(targetItem.starRating, 'fieldLabel', data.fieldLabel);
                        if (data.align !== undefined) this.$set(targetItem.starRating, 'align', data.align);
                        if (data.rating !== undefined) this.$set(targetItem.starRating, 'rating', data.rating);
                        break;
                    case 'ratingScale':
                        targetItem.fieldLabel = data.fieldLabel || targetItem.fieldLabel;
                        targetItem.align = data.align || targetItem.align;
                        targetItem.subLabelOne = data.subLabelOne || targetItem.subLabelOne;
                        targetItem.subLabelTwo = data.subLabelTwo || targetItem.subLabelTwo;
                        if (data.rating !== undefined) targetItem.rating = data.rating;
                        if (!targetItem.ratingScale) this.$set(targetItem, 'ratingScale', {});
                        if (data.fieldLabel !== undefined) this.$set(targetItem.ratingScale, 'fieldLabel', data.fieldLabel);
                        if (data.align !== undefined) this.$set(targetItem.ratingScale, 'align', data.align);
                        if (data.subLabelOne !== undefined) this.$set(targetItem.ratingScale, 'subLabelOne', data.subLabelOne);
                        if (data.subLabelTwo !== undefined) this.$set(targetItem.ratingScale, 'subLabelTwo', data.subLabelTwo);
                        if (data.rating !== undefined) this.$set(targetItem.ratingScale, 'rating', data.rating);
                        break;
                    case 'fileUpload':
                        targetItem.fieldLabelFile = data.fieldLabelFile || targetItem.fieldLabelFile;
                        targetItem.align = data.align || targetItem.align;
                        targetItem.altLabelOne = data.altLabelOne || targetItem.altLabelOne;
                        targetItem.altLabelTwo = data.altLabelTwo || targetItem.altLabelTwo;
                        if (typeof data.isRequired === 'boolean') targetItem.isRequired = data.isRequired;
                        break;
                    case 'image':
                        if (data.asset) targetItem.asset = data.asset;
                        if (data.width) targetItem.imageWidth = data.width;
                        if (data.height) targetItem.imageHeight = data.height;
                        if (data.align) targetItem.align = data.align;
                        break;
                    case 'separationElement':
                        targetItem.separationColor = data.separationColor || targetItem.separationColor;
                        targetItem.separatorStyle = data.separatorStyle || targetItem.separatorStyle;
                        targetItem.separatorHeight = data.separatorHeight || targetItem.separatorHeight;
                        targetItem.separatorBottom = data.separatorBottom || targetItem.separatorBottom;
                        targetItem.separatorTop = data.separatorTop || targetItem.separatorTop;
                        targetItem.separatorSide = data.separatorSide || targetItem.separatorSide;
                        break;
                    case 'send':
                        targetItem.fieldLabel = data.fieldLabel || targetItem.fieldLabel;
                        targetItem.align = data.align || targetItem.align;
                        break;
                    case 'paragraph':
                        if (!targetItem.paragraph) this.$set(targetItem, 'paragraph', {});
                        if (data.content !== undefined) {
                            targetItem.paragraph.content = data.content;
                            console.log('🔵 Paragraph content updated in FormBuilder:', {
                                fieldId: targetItem._id,
                                contentLength: data.content.length,
                                content: data.content.substring(0, 50) + '...'
                            });
                        }
                        if (data.align !== undefined) targetItem.paragraph.align = data.align;
                        if (data.marginTop !== undefined) targetItem.paragraph.marginTop = data.marginTop;
                        if (data.marginBottom !== undefined) targetItem.paragraph.marginBottom = data.marginBottom;
                        break;
                    case 'title':
                        targetItem.titleText = data.titleText || targetItem.titleText;
                        targetItem.subTitle = (data.subTitle !== undefined && data.subTitle !== null) ? data.subTitle : targetItem.subTitle;
                        targetItem.titleSize = data.titleSize || targetItem.titleSize;
                        targetItem.titleAlign = data.titleAlign || targetItem.titleAlign;
                        if (data.slug !== undefined) targetItem.slug = data.slug;
                        if (!targetItem.title) this.$set(targetItem, 'title', {});
                        if (data.titleText !== undefined) targetItem.title.titleText = data.titleText;
                        if (data.subTitle !== undefined) targetItem.title.subTitle = data.subTitle;
                        if (data.titleSize !== undefined) targetItem.title.titleSize = data.titleSize;
                        if (data.titleAlign !== undefined) targetItem.title.titleAlign = data.titleAlign;
                        break;
                }
            }
        },
        async uploadFile(e) {
            let {
                files: [file],
            } = document.getElementById(e.target.id);
            if (!this.beforeUpload(file)) return;
            let path = `${this.site._id}/form/`;
            try {
                let imdUploadData = await uploadAsset({
                    path,
                    name: file.name,
                    size: file.size,
                    type: "form",
                    mime: file.type,
                    ext: file.name.split(".").pop(),
                });
                await axios({
                    method: imdUploadData.data.method,
                    url: imdUploadData.data.url,
                    data: file,
                    headers: { "Content-Type": "application/octet-stream" },
                });
                const formData = {
                    logo: [imdUploadData.data.asset],
                };
                const assetId = imdUploadData.data.assetId;
                await this.updateFormData({
                    siteId: this.site._id,
                    formId: this.formId,
                    formData,
                });

                this.$toasted.success(this.$t("message.successImgUpload"), {
                    duration: 1000,
                    position: "bottom-center",
                });
                this.loadForm();
            } catch (e) {
                this.$toasted.error(this.$t("message.errorImgUpload"), {
                    duration: 1000,
                    position: "bottom-center",
                });
            }
        },
        deleteLogo() {
            this.form.logo = null;
            this.updateFormData({
                siteId: this.site._id,
                formId: this.formId,
                formData: {
                    logo: [],
                },
            });
            this.loadForm();
        },
        selectItem(item) {
            const index = this.formItems.findIndex(formItem => formItem._id === item._id);
            if (index !== -1) {
                this.setActive(index);
                this.openSettings(item, item.type);
            }
        },
        ensureSlugForField(field, allFields) {
            const checkSlugTypes = ['email', 'fullName', 'longText', 'number', 'phone', 'shortText', 'title', 'address', 'datePicker', 'signature', 'captcha', 'paragraph', 'dropDownList', 'singleChoice', 'multipleSelection', 'image', 'fileUpload', 'hour', 'spinner', 'send', 'starRating', 'separationElement', 'ratingScale', 'pagebreak'];

            if (field.type && checkSlugTypes.includes(field.type)) {
                if (!field.slug) {
                    field.slug = field.type;
                }

                const existingField = allFields.find(f =>
                    f._id !== field._id &&
                    f.type === field.type &&
                    f.slug === field.slug &&
                    !f.isDeleted
                );

                if (existingField) {
                    const originalSlug = field.slug;
                    let counter = 2;
                    let newSlug = originalSlug + counter;

                    while (true) {
                        const duplicateCheck = allFields.find(f =>
                            f._id !== field._id &&
                            f.type === field.type &&
                            f.slug === newSlug &&
                            !f.isDeleted
                        );

                        if (!duplicateCheck) {
                            field.slug = newSlug;
                            break;
                        }

                        counter++;
                        newSlug = originalSlug + counter;
                    }
                }
            }
        },
    },
    created() {
        this.loadForm();
    },
    watch: {
        formId: {
            immediate: true,
            handler(newFormId) {
                if (newFormId) {
                    this.loadForm();
                }
            },
        },
        item: {
            handler(newItem) {
                if (newItem && newItem.dropDownList) {
                    this.options = newItem.dropDownList.items || [];
                    this.placeholder = newItem.dropDownList.placeholder || "Seçim Yapınız...";
                }
                if (newItem && newItem.singleChoice) {
                    this.optionsRadio = newItem.singleChoice.items || [];
                }
            },
            immediate: true,
            deep: true,
        },
    },
};
</script>

<style lang="scss" scoped>
#FormBuilder {
    height: 100%;
    width: 100%;
    position: fixed;
    overflow: hidden;

    .header {
        background: linear-gradient(270deg, #f26a52 0%, #e55a42 100%);
        display: flex;
        align-items: center;
        padding: 0 20px;
        justify-content: center;
        box-shadow: 0 2px 8px rgba(242, 106, 82, 0.15);
    }

    .header-text {
        display: flex;
        justify-content: center;
        align-items: center;
        color: #ffffff;
        font-size: 1.2rem;
        font-weight: 500;
    }

    .header-text div {
        padding: 10px 20px;
        cursor: pointer;
    }

    .header-text div:hover {
        background: rgba(255, 255, 255, 0.2);
    }

    .header-text .active {
        background: rgba(255, 255, 255, 0.3);
    }

    #form-builder {
        display: flex;
        height: 100%;
        justify-content: space-between;

        .edit-menu {
            position: relative;
            cursor: pointer;
            height: 100%;
            width: 0px;
            z-index: 999999 !important;

            .edit-menu-open {
                width: 200px;
                background: #f26a52;
                color: #ffffff;
                padding: 15px;
                border-radius: 0px 30px 30px 0px;
                margin-top: 250px;
                display: flex;
                align-items: center;
                transition: all 0.2s ease;

                &:hover {
                    background: #e55a42;
                    transform: translateX(5px);
                }
            }

            .item-menu {
                position: relative;
                left: 0;
                top: 0;
                height: 100%;
                background: #ffffff;
            }
        }

        .edit-form-content {
            width: 900px;
            padding: 20px;
            overflow-y: auto;
            margin-bottom: 100px;

            .add-logo {
                cursor: pointer;
                display: flex;
                align-items: center;
                justify-content: center;
                font-family: Arial, sans-serif;
                color: #6666aa;
                margin-bottom: 20px;

                .line {
                    border-top: 2px dashed #6666aa;
                    margin: 0 10px;
                    width: 100px;
                }

                .text {
                    white-space: nowrap;
                }
            }

            .form-edit {
                padding: 20px;
                background-color: #ffffff;
                border-radius: 10px;
                box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.1);
                min-height: 500px;

                h1 {
                    margin: 0;
                    font-size: 1.5rem;
                    color: #333333;
                }

                h3 {
                    margin: 10px 0 20px;
                    font-size: 1.2rem;
                    color: #666666;
                }

                .form-item {
                    position: relative;
                    margin-bottom: clamp(0.75rem, 1.5vw, 1rem);
                    padding: clamp(0.75rem, 1.5vw, 1rem);
                    border: 2px solid transparent;
                    border-radius: clamp(0.75rem, 1.5vw, 1rem);
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    background: rgba(255, 255, 255, 0.02);

                    &:hover {
                        background: rgba(255, 255, 255, 0.04);
                        border-color: rgba(242, 106, 82, 0.2);
                    }

                    &.active {
                        border-color: #f26a52;
                        background: linear-gradient(145deg,
                            rgba(242, 106, 82, 0.05) 0%,
                            rgba(242, 106, 82, 0.02) 100%);
                        box-shadow: 0 0 0 4px rgba(242, 106, 82, 0.1),
                                    0 8px 25px rgba(242, 106, 82, 0.15);
                        transform: translateY(-2px);
                    }

                    .form-controls {
                        position: absolute;
                        top: clamp(0.75rem, 1.5vw, 1rem);
                        right: clamp(0.75rem, 1.5vw, 1rem);
                        display: none;
                        flex-direction: row;
                        gap: clamp(0.375rem, 1vw, 0.5rem);
                        z-index: 10;

                        .md-icon {
                            width: clamp(2rem, 4vw, 2.25rem);
                            height: clamp(2rem, 4vw, 2.25rem);
                            background: rgba(255, 255, 255, 0.95);
                            border: 1px solid rgba(226, 232, 240, 0.8);
                            border-radius: 50%;
                            padding: 0;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            font-size: clamp(1rem, 2vw, 1.125rem);
                            color: rgba(107, 114, 128, 0.7);
                            cursor: pointer;
                            transition: all 0.2s ease;
                            backdrop-filter: blur(8px);
                            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

                            &:hover {
                                background: rgba(255, 255, 255, 1);
                                border-color: rgba(203, 213, 225, 0.8);
                                color: rgba(55, 65, 81, 0.9);
                                transform: scale(1.05);
                                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                            }

                            &:first-child {
                                color: rgba(239, 68, 68, 0.7);

                                &:hover {
                                    color: #ef4444;
                                    border-color: rgba(239, 68, 68, 0.3);
                                    background: rgba(254, 242, 242, 0.95);
                                }
                            }

                            &:last-child {
                                color: rgba(242, 106, 82, 0.7);

                                &:hover {
                                    color: #f26a52;
                                    border-color: rgba(242, 106, 82, 0.3);
                                    background: rgba(255, 247, 244, 0.95);
                                }
                            }
                        }
                    }

                    .form-controls.visible {
                        display: flex;
                    }
                }

                // Drop indicator styles
                .drop-indicator {
                    position: absolute;
                    left: 0;
                    right: 0;
                    height: 3px;
                    background: linear-gradient(90deg, #f26a52, #e55a42);
                    border-radius: 2px;
                    box-shadow: 0 0 8px rgba(242, 106, 82, 0.6);
                    z-index: 1000;
                    opacity: 0.9;
                    animation: pulse 1.5s infinite;
                    pointer-events: none; // Hover tespitinde engel olmasın

                    &.drop-before {
                        top: -2px;
                    }

                    &.drop-after {
                        bottom: -2px;
                    }
                }

                @keyframes pulse {
                    0%, 100% {
                        opacity: 0.9;
                        transform: scaleY(1);
                    }
                    50% {
                        opacity: 1;
                        transform: scaleY(1.2);
                    }
                }
            }
        }

        .right-menu {
            .settings-menu {
                position: fixed;
                right: 0;
                max-width: 280px !important;
                height: 100%;
                width: 100%;
            }
        }
    }

    // Tablet responsive
    @media (max-width: 1200px) {
        #form-builder {
            grid-template-columns: 260px 1fr 300px;
        }
    }

    @media (max-width: 768px) {
        #form-builder {
            grid-template-columns: 1fr;

            .edit-menu {
                display: none;
            }

            .right-menu {
                display: none;
            }
        }
    }
}

.slide-enter-active,
.slide-leave-active {
    transition: all 0.3s ease;
}

.slide-enter,
.slide-leave-to {
    transform: translateX(-100%);
    opacity: 0;
}

.slideRight-enter-active,
.slide-leave-active {
    transition: transform 0.3s ease-in-out;
}

.slideRight-enter .slideRight-leave-to {
    transform: translateX(-100%);
    opacity: 0;
}

.form-group {
    margin-bottom: 1rem;
}

.form-group label {
    display: block;
    font-weight: bold;
    margin-bottom: 0.5rem;
    color: #333;
}

.form-group input[type="number"] {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 1rem;
    color: #333;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    transition: border-color 0.3s ease;
}

.form-group input[type="number"]:focus {
    border-color: #3f51b5;
    outline: none;
    box-shadow: 0 0 5px rgba(63, 81, 181, 0.5);
}

//.slideRight-leave-to {
//    animation: slideOut 0.3s forwards;
//}
</style>
